import { palette, spacing, radius, typography } from '@/styles/tokens';

import React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity, Image, TextInput, Alert, Modal, KeyboardAvoidingView, Platform, Keyboard, DeviceEventEmitter, ActivityIndicator, useWindowDimensions } from 'react-native';
import { SEOHead } from '@/components/SEOHead';

import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { User, Globe, Bell, Settings, Shield, FileText, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Pencil, Calendar, Mail, X, Check, FingerprintPattern as Fingerprint, Phone, UserX, Cookie, Scale, DollarSign, MessageCircle, Camera, Trash2, Image as ImageIcon, HardDrive, Trash, RefreshCw } from 'lucide-react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { trackPageView } from '@/src/analytics';
import { setCustomIdContext } from '@/src/analytics/customIdContext';
import { Image as RNImage } from 'react-native';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { Linking } from 'react-native';
import { useTranslation } from '@/hooks/useTranslation';
import * as Localization from 'expo-localization';
import { useGamification } from '@/hooks/useGamification';
import { LanguageSelector } from '@/components/LanguageSelector';
import { CustomPaywall } from '@/components/CustomPaywall';
import { WebPaymentModal } from '@/components/WebPaymentModal';
import { supabase } from '@/lib/supabase';
import RNDateTimePicker from '@react-native-community/datetimepicker';
import { useDidomiContext } from '@/components/DidomiProvider';
import { useCountry } from '@/hooks/useCountry';
import { useCampaignConfig } from '@/hooks/useCampaignConfig';
import Constants from 'expo-constants';
import { getApiUrl, getSupabaseAnonKey, getSupabaseUrl } from '@/lib/apiConfig';
import {
  formatDateForDisplay,
  parseDateFromDisplay,
  validateDisplayDate,
  getDateExample,
  getDatePattern,
  getLocalizedDatePattern,
  dateToISOString
} from '@/utils/dateFormatting';
import { validatePseudo } from '@/utils/pseudoValidator';
import { validateEmail, validatePhoneNumber } from '@/utils/contactValidators';
import { generateDefaultPseudo } from '@/utils/pseudoGenerator';
import {
  pickImageFromLibrary,
  takePhotoWithCamera,
  uploadProfilePicture,
  updateUserProfilePicture,
  removeProfilePicture,
  deleteOldProfilePicture,
} from '@/services/profilePictureService';
import { notificationPreferencesService, NotificationPreferencesWithStatus } from '@/services/notificationPreferencesService';
import { imageCacheService } from '@/services/imageCacheService';
import { ImageCacheStats } from '@/types';
import { getContentBottomPadding } from '@/utils/tabBarPadding';
import { modalEvents } from '@/utils/modalEvents';
import { appVersionService, UpdateCheckResult } from '@/services/appVersionService';
import { ForceUpdateModal } from '@/components/ForceUpdateModal';
import { RecommendedUpdateModal } from '@/components/RecommendedUpdateModal';
import { useAuthModals } from '@/contexts/AuthModalsContext';
import { OTPLoginFlow } from '@/components/OTPLoginFlow';
import { DCBPaymentFlow } from '@/components/DCBPaymentFlow';
import { PremiumSubscriptionCard, PaymentMethod } from '@/components/PremiumSubscriptionCard';
import { usePlayer } from '@/contexts/PlayerContext';
import { useDebugTracking } from '@/contexts/DebugTrackingContext';
import { REVENUECAT_CONFIG } from '@/utils/revenueCatConfig';
import { EyeOff, Eye } from 'lucide-react-native';
import { useTheme } from '@/hooks/useTheme';

// The profile screen re-renders (and this effect re-runs) several times per
// visit, which used to fire dcb-subscription-status in bursts. Concurrent
// invocations raced on the single-use Smartuser /token/refresh and produced
// TOKEN_INVALID / TOKEN_MANAGER_CACHE_ERROR noise. Cache the result per user
// for the session and dedupe in-flight calls.
type DcbSubscriptionDisplayInfo = { nextPaymentDate: string; period: string } | null;

const DCB_STATUS_CACHE_TTL_MS = 10 * 60 * 1000;
let dcbStatusCache: { smartuserId: string; fetchedAt: number; info: DcbSubscriptionDisplayInfo } | null = null;
let dcbStatusInFlight: { smartuserId: string; promise: Promise<DcbSubscriptionDisplayInfo> } | null = null;

function fetchDcbSubscriptionInfo(smartuserId: string): Promise<DcbSubscriptionDisplayInfo> {
  if (
    dcbStatusCache
    && dcbStatusCache.smartuserId === smartuserId
    && Date.now() - dcbStatusCache.fetchedAt < DCB_STATUS_CACHE_TTL_MS
  ) {
    return Promise.resolve(dcbStatusCache.info);
  }

  if (dcbStatusInFlight && dcbStatusInFlight.smartuserId === smartuserId) {
    return dcbStatusInFlight.promise;
  }

  const promise = (async (): Promise<DcbSubscriptionDisplayInfo> => {
    try {
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/dcb-subscription-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getSupabaseAnonKey()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ smartuserId }),
      });
      const data = await response.json();
      const info: DcbSubscriptionDisplayInfo =
        data?.success && data.isPaying && data.nextPaymentDate
          ? { nextPaymentDate: data.nextPaymentDate, period: data.period || 'unknown' }
          : null;
      dcbStatusCache = { smartuserId, fetchedAt: Date.now(), info };
      return info;
    } catch {
      // Network failure: don't cache, the next profile visit retries.
      return null;
    } finally {
      dcbStatusInFlight = null;
    }
  })();

  dcbStatusInFlight = { smartuserId, promise };
  return promise;
}

function ProfileScreenMobile() {
  const theme = useTheme();
  const primaryColor = theme.colors.primary;
  const { t, language, isRTL } = useTranslation();
  const { width: windowWidth } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && windowWidth >= 768;
  const localParams = useLocalSearchParams<{ operationId?: string }>();
  const operationId = useMemo(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('operationId') || undefined;
    }
    return localParams.operationId;
  }, [localParams.operationId, Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.search : '']);
  const hasOperationId = Platform.OS === 'web' && !!operationId;

  // snp_csid extraction from the URL happens at app bootstrap (app/_layout.tsx),
  // so it is captured on any landing route, not only /profile.

  const paymentSuccess = useMemo(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('payment_success') === 'true';
    }
    return false;
  }, []);

  const [paymentReturnStatus, setPaymentReturnStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>(
    paymentSuccess ? 'verifying' : 'idle'
  );
  const paymentReturnHandledRef = useRef(false);

  const getLocaleForDatePicker = () => {
    const localeMap: Record<string, string> = {
      'en': 'en-US',
      'fr': 'fr-FR',
      'es': 'es-ES',
      'it': 'it-IT',
    };
    return localeMap[language] || 'en-US';
  };

  useFocusEffect(
    React.useCallback(() => {
      trackPageView('Profile', '/(tabs)/profile');

      const checkForUpdates = async () => {
        try {
          const result = await appVersionService.checkForUpdates(true, false, true);
          if (result) {
            setUpdateCheckResult(result);
            if (result.forceUpdateRequired) {
              setShowForceUpdateModal(true);
              setShowRecommendedUpdateModal(false);
            } else if (result.recommendedUpdateAvailable) {
              setShowRecommendedUpdateModal(true);
              setShowForceUpdateModal(false);
            } else {
              setShowForceUpdateModal(false);
              setShowRecommendedUpdateModal(false);
            }
          }
        } catch (error) {
          console.error('🔄 [PROFILE] Error checking for updates:', error);
        }
      };

      checkForUpdates();
    }, [])
  );
  const insets = useSafeAreaInsets();
  const { authState, logout, checkBiometricSupport, enableBiometricLogin, disableBiometricLogin, isBiometricEnabled, updateUserSubscription, updateUserPaymentMethod, loginWithOTPData } = useAuth();
  const { isSubscribed, purchaseSubscription, restorePurchases, isPurchasing, products, isIAPReady, checkSubscriptionStatus, forceRefreshSubscriptionFromDatabase, subscriptionState, gracePeriodEndDate } = useSubscription();
  const { processEvent, userGamification } = useGamification();
  const { showPreferences } = useDidomiContext();
  const { countryCode, countryName, isLoading: countryLoading, error: countryError, refetch: refetchCountry } = useCountry();
  const { campaignCountriesLanguagesId, isLoading: campaignLoading, isAvailable, error: campaignError, availableLanguages, refetch: refetchCampaign, freeEpisodesCount, revenueCatEnabledApp, revenueCatEnabledWeb, dcbEnabledWeb } = useCampaignConfig();
  const { isVisible: playerIsVisible } = usePlayer();
  const { isSnowplowDisabled, toggleSnowplow, setDebugModeActive } = useDebugTracking();
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [userProfile, setUserProfile] = useState({
    pseudo: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '', // Always stored in ISO format (YYYY-MM-DD)
    profilePictureUrl: '',
  });
  const [editingProfile, setEditingProfile] = useState({
    pseudo: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '', // Always stored in ISO format (YYYY-MM-DD)
    profilePictureUrl: '',
  });
  const [pseudoError, setPseudoError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isCheckingPseudo, setIsCheckingPseudo] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [biometricSupport, setBiometricSupport] = useState<{ isAvailable: boolean; isEnrolled: boolean; supportedTypes: string[] }>({ isAvailable: false, isEnrolled: false, supportedTypes: [] });
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showWebPaymentModal, setShowWebPaymentModal] = useState(false);
  const [webPaymentUrl, setWebPaymentUrl] = useState('');
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showActiveSubErrorModal, setShowActiveSubErrorModal] = useState(false);
  const [showGracePeriodModal, setShowGracePeriodModal] = useState(false);
  const [showImagePickerModal, setShowImagePickerModal] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadImageError, setUploadImageError] = useState<string | null>(null);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showLocalStorageModal, setShowLocalStorageModal] = useState(false);
  const [showDebugTools, setShowDebugTools] = useState(false);
  const [recentAchievements, setRecentAchievements] = useState<Array<{
    id: string;
    achievement_type: string;
    coins_earned: number;
    created_at: string;
    metadata?: any;
  }>>([]);
  const [versionTapCount, setVersionTapCount] = useState(0);
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferencesWithStatus | null>(null);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [cacheStats, setCacheStats] = useState<ImageCacheStats | null>(null);
  const [isLoadingCacheStats, setIsLoadingCacheStats] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);
  const [showForceUpdateModal, setShowForceUpdateModal] = useState(false);
  const [showRecommendedUpdateModal, setShowRecommendedUpdateModal] = useState(false);
  const [updateCheckResult, setUpdateCheckResult] = useState<UpdateCheckResult | null>(null);
  const [expandedCookiesSection, setExpandedCookiesSection] = useState(false);
  const [expandedLegalSection, setExpandedLegalSection] = useState(false);
  const { showSignInModal, showSignUpModal } = useAuthModals();
  const [asyncStorageStats, setAsyncStorageStats] = useState<{
    rubriquesCount: number;
    seriesCount: number;
    freeEpisodesCount: number;
    episodesCount: number;
    cachedSeriesCount: number;
    totalCachedEpisodes: number;
    avgEpisodesPerSeries: number;
    // Prefetch tracking
    lastFullPrefetchTimestamp: number | null;
    lastFullPrefetchCampaignId: string | null;
    lastFullPrefetchAge: string | null;
    prefetchMode: 'full' | 'incremental' | 'none';
    prefetchInProgress: boolean;
    prefetchStats: {
      totalSeriesPrefetched: number;
      cacheHitsCount: number;
      cacheMissesCount: number;
      fullPrefetchTriggered: boolean;
      incrementalPrefetchTriggered: boolean;
      durationMs: number | null;
    } | null;
    // Cache stats
    cacheStatsData: {
      cacheHits: number;
      cacheMisses: number;
      apiCalls: number;
      hitRate: number;
    } | null;
    // Series details
    cachedSeriesList: Array<{
      seriesId: string;
      episodeCount: number;
      cacheAge: string;
      timestamp: number;
    }>;
    // Episode URLs for debugging
    episodeUrls: Array<{
      seriesId: string;
      episodeId: string;
      episodeTitle: string;
      hasVideoUrl: boolean;
      hasDrmUrl: boolean;
      videoUrl: string | null;
      drmUrl: string | null;
    }>;
  } | null>(null);
  const [dcbCookieDebug, setDcbCookieDebug] = useState<Record<string, any> | null>(null);
  const [dcbCookieLoading, setDcbCookieLoading] = useState(false);
  const lastDateChangeRef = useRef<{ date: string; timestamp: number } | null>(null);
  const isProcessingDateChangeRef = useRef(false);
  const versionTapTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const profileScrollViewRef = useRef<ScrollView>(null);
  const mainScrollViewRef = useRef<ScrollView>(null);
  const inputPositionsRef = useRef<{ [key: string]: number }>({});
  const wasUnauthenticatedRef = useRef(!authState.user);
  const dcbFlowCompletedRef = useRef(false);

  // DCB auto-signup: a not-logged-in visitor arriving with a valid operationId is
  // created + logged in automatically (no OTP screen), reusing dcb_create_user +
  // loginWithOTPData. Kill-switch: set to false to fall back to the manual OTP flow.
  const DCB_AUTO_SIGNUP_ENABLED = true;
  const autoSignupAttemptedRef = useRef(false);
  const [autoSignupInProgress, setAutoSignupInProgress] = useState(false);

  const [dcbFlowData, setDcbFlowData] = useState<{
    isNewUser: boolean;
    identifier: string;
    identityType: 'EMAIL' | 'MSISDN';
    sessionToken: string;
    smartuserId: string;
    supabaseUserId: string;
  } | null>(null);
  const [showDCBFlow, setShowDCBFlow] = useState(false);

  const [dcbValidationStatus, setDcbValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [dcbPrivilegedLoginData, setDcbPrivilegedLoginData] = useState<{
    dcbSessionToken: string;
    dcbAutologinToken: string;
    dcbTemporaryAbsoluteReference: string;
    identities?: Array<{ type: string; identifier: string }>;
  } | null>(null);
  const [dcbValidationError, setDcbValidationError] = useState('');

  const [showDcbUnsubModal, setShowDcbUnsubModal] = useState(false);
  const [isDcbUnsubscribing, setIsDcbUnsubscribing] = useState(false);
  const [dcbUnsubResult, setDcbUnsubResult] = useState<'success' | 'error' | null>(null);
  const [dcbSubscriptionInfo, setDcbSubscriptionInfo] = useState<{
    nextPaymentDate: string;
    period: string;
  } | null>(null);

  // Cleanup version tap timeout on unmount
  useEffect(() => {
    return () => {
      if (versionTapTimeoutRef.current) {
        clearTimeout(versionTapTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!paymentSuccess || paymentReturnHandledRef.current) return;
    if (!authState.user?.smartuserId) return;

    paymentReturnHandledRef.current = true;

    const cleanPaymentSuccessFromUrl = () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.delete('payment_success');
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    };

    const verifyPayment = async () => {
      setPaymentReturnStatus('verifying');
      cleanPaymentSuccessFromUrl();

      try {
        updateUserSubscription(true);
        updateUserPaymentMethod('stripe');

        const confirmed = await forceRefreshSubscriptionFromDatabase();
        if (confirmed) {
          setPaymentReturnStatus('success');
        } else {
          await checkSubscriptionStatus();
          setPaymentReturnStatus('success');
        }
      } catch (error) {
        console.error('[Profile] Payment verification error:', error);
        setPaymentReturnStatus('success');
      }
    };

    verifyPayment();
  }, [paymentSuccess, authState.user?.smartuserId]);

  // DCB: Validate operationId immediately on arrival by calling dcb_privileged_login
  useEffect(() => {
    if (!hasOperationId || !operationId) return;
    if (dcbValidationStatus !== 'idle') return;

    const validateDcbOperation = async () => {
      setDcbValidationStatus('validating');
      console.log('[Profile-DCB] Validating operationId immediately...', operationId);

      try {
        const response = await fetch(`${getSupabaseUrl()}/functions/v1/manage-users`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getSupabaseAnonKey()}`,
          },
          body: JSON.stringify({
            action: 'dcb_privileged_login',
            operationId,
          }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          const errorCode = data.error || '';
          const isPaymentNotFound = response.status === 404 || errorCode === 'payment_not_found' || errorCode === 'PAYMENT_NOT_FOUND';
          if (isPaymentNotFound) {
            console.warn('[Profile-DCB] operationId not found (invalid or expired):', errorCode);
          } else {
            console.error('[Profile-DCB] operationId validation failed:', errorCode);
          }
          setDcbValidationStatus('invalid');
          setDcbValidationError(data.message || data.error || t('otp.dcbPaymentNotFound'));
          return;
        }

        console.log('[Profile-DCB] operationId validated successfully, full response:', JSON.stringify(data, null, 2));
        setDcbPrivilegedLoginData({
          dcbSessionToken: data.sessionToken,
          dcbAutologinToken: data.autologinToken || '',
          dcbTemporaryAbsoluteReference: data.temporaryAbsoluteReference,
          identities: Array.isArray(data.identities) ? data.identities : [],
        });
        setDcbValidationStatus('valid');

        if (Platform.OS === 'web' && operationId) {
          try {
            localStorage.setItem('dcb_last_operation_id', operationId);
            localStorage.setItem('dcb_operation_id_stored_at', String(Date.now()));
            console.log('[Profile-DCB] Persisted operationId to localStorage');
          } catch {}
        }
      } catch (error) {
        console.error('[Profile-DCB] operationId validation error:', error);
        setDcbValidationStatus('invalid');
        setDcbValidationError(t('otp.dcbPaymentNotFound'));
      }
    };

    validateDcbOperation();
  }, [hasOperationId, operationId]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const handleDcbMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'dcb_callback') return;

      // The popup closes right after posting this message, so the snp_csid
      // must be persisted here — it never travels through a URL in this path.
      if (event.data.snp_csid) {
        setCustomIdContext(event.data.snp_csid);
      }

      if (event.data.status === 'ok' && event.data.operationId) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('operationId', event.data.operationId);
        window.location.href = currentUrl.toString();
      }
    };

    window.addEventListener('message', handleDcbMessage);
    return () => window.removeEventListener('message', handleDcbMessage);
  }, []);

  // Scroll to top when Profile tab is pressed
  useEffect(() => {
    const scrollToTop = () => {
      mainScrollViewRef.current?.scrollTo({ y: 0, animated: true });
    };

    if (Platform.OS === 'web') {
      window.addEventListener('profileTabPressed', scrollToTop);
      return () => window.removeEventListener('profileTabPressed', scrollToTop);
    } else {
      const subscription = DeviceEventEmitter.addListener('profileTabPressed', scrollToTop);
      return () => subscription.remove();
    }
  }, []);

  // Redirect to home when user transitions from unauthenticated to authenticated
  // This handles Apple/Google sign-in where the callback may not execute due to component re-render
  // Skip redirect when DCB operationId or payment_success is present - those flows need to run first
  useEffect(() => {
    if (hasOperationId || paymentSuccess) return;
    if (authState.user && wasUnauthenticatedRef.current) {
      wasUnauthenticatedRef.current = false;
      router.replace('/(tabs)');
    } else if (!authState.user) {
      wasUnauthenticatedRef.current = true;
    }
  }, [authState.user, hasOperationId]);

  // UC3: User already logged in and arrives with operationId - start DCB flow automatically
  // Wait for dcbValidationStatus to be 'valid' so DCBPaymentFlow gets pre-fetched tokens
  useEffect(() => {
    if (hasOperationId && authState.user && dcbValidationStatus === 'valid' && dcbPrivilegedLoginData && !showDCBFlow && !dcbFlowData && !dcbFlowCompletedRef.current) {
      setDcbFlowData({
        isNewUser: false,
        identifier: authState.user.email || authState.user.phone_number || '',
        identityType: authState.user.email ? 'EMAIL' : 'MSISDN',
        sessionToken: '',
        smartuserId: authState.user.smartuserId || '',
        supabaseUserId: authState.user.id || '',
      });
      setShowDCBFlow(true);
    }
  }, [hasOperationId, authState.user, dcbValidationStatus, dcbPrivilegedLoginData]);

  // Auto-signup (NOT logged in + valid operationId): create-or-login the user from the
  // already-resolved privileged data and skip the OTP screen entirely. dcb_create_user
  // handles both new and existing (by MSISDN) users, sets is_paying and claims the
  // operationId; loginWithOTPData then establishes the session. On ANY failure we fall
  // through to the manual OTPLoginFlow, so there is no regression.
  useEffect(() => {
    if (
      !DCB_AUTO_SIGNUP_ENABLED ||
      Platform.OS !== 'web' ||
      !hasOperationId ||
      !operationId ||
      authState.user ||
      dcbValidationStatus !== 'valid' ||
      !dcbPrivilegedLoginData ||
      autoSignupAttemptedRef.current
    ) {
      return;
    }
    autoSignupAttemptedRef.current = true;

    const runAutoSignup = async () => {
      setAutoSignupInProgress(true);
      try {
        const ids = dcbPrivilegedLoginData.identities || [];
        const identity = ids.find((i) => i.type === 'MSISDN') || ids.find((i) => i.type === 'EMAIL');
        if (!identity) {
          throw new Error('No identity resolved from operationId');
        }

        const response = await fetch(`${getSupabaseUrl()}/functions/v1/manage-users`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getSupabaseAnonKey()}`,
          },
          body: JSON.stringify({
            action: 'dcb_create_user',
            operationId,
            paymentProvider: 'DVP4M',
            paymentSessionToken: dcbPrivilegedLoginData.dcbSessionToken,
            autologinToken: dcbPrivilegedLoginData.dcbAutologinToken,
            temporaryAbsoluteReference: dcbPrivilegedLoginData.dcbTemporaryAbsoluteReference,
            identifier: identity.identifier,
            identityType: identity.type,
            suggestedCountry: countryCode,
            dcbIdentities: JSON.stringify(dcbPrivilegedLoginData.identities),
          }),
        });

        const data = await response.json();
        if (!response.ok || !data.success || !data.user || !data.sessionToken) {
          throw new Error(data.error || data.message || 'dcb_create_user failed');
        }

        const loginResult = await loginWithOTPData(data.user, data.sessionToken, data.isNewUser || false);
        if (!loginResult.success) {
          throw new Error(loginResult.error || 'session establishment failed');
        }

        // Account created/attached, subscription activated and operationId claimed by
        // dcb_create_user — mark the flow done so UC3 doesn't try to re-activate.
        dcbFlowCompletedRef.current = true;
        console.log('[Profile-DCB] Auto-signup succeeded (isNewUser:', data.isNewUser, ')');

        if (typeof window !== 'undefined') {
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('operationId');
            url.searchParams.delete('dcb_ok');
            window.history.replaceState({}, '', url.toString());
          } catch {}
        }
      } catch (err) {
        // Fall back to the manual OTP flow (OTPLoginFlow renders since authState.user is still null).
        console.warn('[Profile-DCB] Auto-signup failed, falling back to manual OTP flow:', err);
      } finally {
        setAutoSignupInProgress(false);
      }
    };

    runAutoSignup();
  }, [hasOperationId, operationId, authState.user, dcbValidationStatus, dcbPrivilegedLoginData]);

  // Load user profile data
  useEffect(() => {
    if (authState.user?.smartuserId) {
      loadUserProfile();
    } else {
      // Clear profile data when user logs out
      const emptyProfile = {
        pseudo: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        profilePictureUrl: '',
      };
      setUserProfile(emptyProfile);
      setEditingProfile(emptyProfile);
    }
  }, [authState.user?.smartuserId]);

  // Load notification preferences (always, even for guests)
  useEffect(() => {
    loadNotificationPreferences();
  }, [authState.user?.smartuserId]);

  // Load cache statistics (works even without user login)
  useEffect(() => {
    loadCacheStats();
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const styleId = 'profile-date-picker-styles';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      input[type="date"]::-webkit-calendar-picker-indicator {
        opacity: 0;
        position: absolute;
        right: 0;
        top: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // Check biometric support and status
  useEffect(() => {
    const checkBiometrics = async () => {
      console.log('🔐 [Profile] Biometric useEffect triggered', {
        hasUser: !!authState.user,
        userId: authState.user?.smartuserId,
        platform: Platform.OS,
        language: language,
      });

      if (authState.user) {
        console.log('🔐 [Profile] User exists, calling checkBiometricSupport...');
        try {
          const support = await checkBiometricSupport();
          console.log('🔐 [Profile] checkBiometricSupport returned:', JSON.stringify(support));
          setBiometricSupport(support);

          if (support.isAvailable) {
            console.log('🔐 [Profile] Biometric available, checking if enabled...');
            const enabled = await isBiometricEnabled();
            console.log('🔐 [Profile] Biometric enabled status:', enabled);
            setBiometricEnabled(enabled);
          } else {
            console.log('🔐 [Profile] Biometric NOT available, skipping enabled check');
          }
        } catch (error) {
          console.error('🔐 [Profile] Error in checkBiometrics:', error);
        }
      } else {
        console.log('🔐 [Profile] No user, skipping biometric check');
      }
    };

    checkBiometrics();
  }, [authState.user, language]);

  const loadUserProfile = async () => {
    if (!authState.user?.smartuserId) return;

    try {
      const { data, error } = await supabase
        .from('users')
        .select('pseudo, email, phone_number, date_of_birth, profile_picture_url')
        .eq('smartuser_id', authState.user.smartuserId)
        .single();

      if (error) {
        console.error('Error loading user profile:', error);
        return;
      }

      const profile = {
        pseudo: data.pseudo || '',
        email: data.email || '',
        phoneNumber: data.phone_number || '',
        dateOfBirth: data.date_of_birth || '',
        profilePictureUrl: data.profile_picture_url || '',
      };

      setUserProfile(profile);
      setEditingProfile(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const loadNotificationPreferences = async () => {
    try {
      const preferences = await notificationPreferencesService.getPreferences(
        authState.user?.smartuserId
      );
      setNotificationPreferences(preferences);
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    }
  };

  const loadCacheStats = async () => {
    setIsLoadingCacheStats(true);
    try {
      await imageCacheService.initialize();
      const stats = await imageCacheService.getCacheStats();
      setCacheStats(stats);
    } catch (error) {
      console.error('Error loading cache stats:', error);
    } finally {
      setIsLoadingCacheStats(false);
    }
  };


  const handleClearCache = async () => {
    Alert.alert(
      t('clearImageCache'),
      t('clearImageCacheConfirmation'),
      [
        {
          text: t('cancel'),
          style: 'cancel',
        },
        {
          text: t('clearCache'),
          style: 'destructive',
          onPress: async () => {
            setIsClearingCache(true);
            try {
              const clearedCount = await imageCacheService.clearAllCache();
              Alert.alert(
                t('success'),
                t('cacheCleared', { count: clearedCount })
              );
              await loadCacheStats();
            } catch (error) {
              console.error('Error clearing cache:', error);
              Alert.alert(t('error'), t('failedToClearCache'));
            } finally {
              setIsClearingCache(false);
            }
          },
        },
      ]
    );
  };

  const handleClearExpiredCache = async () => {
    setIsClearingCache(true);
    try {
      const clearedCount = await imageCacheService.clearExpiredCache();
      if (clearedCount > 0) {
        Alert.alert(
          t('success'),
          t('expiredCacheCleared', { count: clearedCount })
        );
      } else {
        Alert.alert(t('info'), t('noExpiredCache'));
      }
      await loadCacheStats();
    } catch (error) {
      console.error('Error clearing expired cache:', error);
      Alert.alert(t('error'), t('failedToClearCache'));
    } finally {
      setIsClearingCache(false);
    }
  };

  const handleToggleNotificationPreference = async (
    key: keyof Omit<NotificationPreferencesWithStatus, 'push_enabled' | 'can_request_permission'>
  ) => {
    if (!notificationPreferences) return;

    const newValue = !notificationPreferences[key];
    const updatedPreferences = {
      ...notificationPreferences,
      [key]: newValue,
    };

    setNotificationPreferences(updatedPreferences);

    const success = await notificationPreferencesService.updatePreferences(
      authState.user?.smartuserId,
      { [key]: newValue }
    );

    if (!success) {
      setNotificationPreferences(notificationPreferences);
      Alert.alert(t('error'), t('failedToUpdatePreferences'));
    }
  };

  const handleRequestPushPermission = async () => {
    setIsLoadingPreferences(true);
    try {
      const granted = await notificationPreferencesService.requestPushPermission();
      if (granted) {
        await loadNotificationPreferences();
        Alert.alert(t('success'), t('pushNotificationsEnabled'));
      } else {
        Alert.alert(
          t('pushNotificationsDisabled'),
          t('enablePushNotificationsInSettings'),
          [
            { text: t('cancel'), style: 'cancel' },
            {
              text: t('openSettings'),
              onPress: () => notificationPreferencesService.openSystemSettings(),
            },
          ]
        );
      }
    } catch (error) {
      console.error('Error requesting push permission:', error);
      Alert.alert(t('error'), t('failedToRequestPermission'));
    } finally {
      setIsLoadingPreferences(false);
    }
  };

  const handleOpenNotificationSettings = async () => {
    try {
      await notificationPreferencesService.openSystemSettings();
    } catch (error) {
      console.error('[Profile] Error opening notification settings:', error);
      Alert.alert(t('error'), t('errorOpeningSettings'));
    }
  };

  const handleDateChange = (event: any, selectedDate?: Date) => {
    const eventType = event.type;
    const now = Date.now();

    console.log('[DatePicker] handleDateChange called:', {
      platform: Platform.OS,
      eventType,
      selectedDate: selectedDate?.toISOString(),
      isProcessing: isProcessingDateChangeRef.current,
      timestamp: now
    });

    if (Platform.OS === 'android') {
      if (eventType === 'dismissed') {
        console.log('[DatePicker] User dismissed picker');
        setShowDatePicker(false);
        setShowProfileEditor(true);
        lastDateChangeRef.current = null;
        isProcessingDateChangeRef.current = false;
        return;
      }

      if (eventType === 'set' && selectedDate) {
        // Convert to ISO format (YYYY-MM-DD) for storage
        const isoDate = dateToISOString(selectedDate);

        console.log('[DatePicker] Date set event:', {
          isoDate,
          displayFormat: formatDateForDisplay(isoDate, countryCode),
          lastDate: lastDateChangeRef.current?.date,
          lastTimestamp: lastDateChangeRef.current?.timestamp,
          timeDiff: lastDateChangeRef.current ? now - lastDateChangeRef.current.timestamp : null
        });

        if (isProcessingDateChangeRef.current) {
          console.log('[DatePicker] Already processing, ignoring duplicate event');
          return;
        }

        if (
          lastDateChangeRef.current &&
          lastDateChangeRef.current.date === isoDate &&
          now - lastDateChangeRef.current.timestamp < 1000
        ) {
          console.log('[DatePicker] Duplicate date change detected within 1s, ignoring');
          return;
        }

        isProcessingDateChangeRef.current = true;
        lastDateChangeRef.current = { date: isoDate, timestamp: now };

        console.log('[DatePicker] Processing date change (ISO):', isoDate);
        setEditingProfile(prev => ({ ...prev, dateOfBirth: isoDate }));
        setShowDatePicker(false);
        setShowProfileEditor(true);

        setTimeout(() => {
          isProcessingDateChangeRef.current = false;
        }, 500);
        return;
      }
    }

    if (selectedDate) {
      // Convert to ISO format (YYYY-MM-DD) for storage
      const isoDate = dateToISOString(selectedDate);
      console.log('[DatePicker] iOS date change (ISO):', isoDate);
      setEditingProfile(prev => ({ ...prev, dateOfBirth: isoDate }));
    }
  };

  const confirmDateSelection = () => {
    setShowDatePicker(false);
    setShowProfileEditor(true);
  };

  const cancelDateSelection = () => {
    setShowDatePicker(false);
    setShowProfileEditor(true);
  };

  const openDatePicker = () => {
    if (Platform.OS === 'web') {
      const dateInput = document.querySelector('input[type="date"]') as HTMLInputElement;
      if (dateInput) {
        dateInput.focus();
        dateInput.showPicker?.();
      }
    } else {
      console.log('[DatePicker] Opening date picker');
      lastDateChangeRef.current = null;
      isProcessingDateChangeRef.current = false;
      setShowProfileEditor(false);
      setShowDatePicker(true);
    }
  };

  const getDateValue = () => {
    if (editingProfile.dateOfBirth) {
      // Parse ISO date (YYYY-MM-DD) correctly
      const [year, month, day] = editingProfile.dateOfBirth.split('-').map(Number);
      // Month is 0-indexed in JavaScript Date
      return new Date(year, month - 1, day);
    }
    // Return a default date (e.g., 30 years ago)
    const defaultDate = new Date();
    defaultDate.setFullYear(defaultDate.getFullYear() - 30);
    return defaultDate;
  };

  const openProfileEditor = () => {
    setEditingProfile(userProfile);
    setPseudoError('');
    setEmailError('');
    setPhoneError('');
    setShowProfileEditor(true);
  };

  const scrollToInput = (inputName: string) => {
    const yPosition = inputPositionsRef.current[inputName];
    if (yPosition !== undefined && profileScrollViewRef.current) {
      profileScrollViewRef.current.scrollTo({
        y: Math.max(0, yPosition - 20),
        animated: true,
      });
    }
  };

  const handleInputLayout = (inputName: string, event: any) => {
    const { y } = event.nativeEvent.layout;
    inputPositionsRef.current[inputName] = y;
  };

  const updateUserProfile = async () => {
    if (!authState.user?.smartuserId) return;

    setIsUpdating(true);
    try {
      const previousProfile = { ...userProfile };
      console.log('[Profile] Previous profile:', previousProfile);
      console.log('[Profile] New profile:', editingProfile);

      const hasPseudoChanged = editingProfile.pseudo !== previousProfile.pseudo;
      const hasEmailChanged = editingProfile.email !== previousProfile.email;
      const hasPhoneChanged = editingProfile.phoneNumber !== previousProfile.phoneNumber;
      const hasBirthDateChanged = editingProfile.dateOfBirth !== previousProfile.dateOfBirth;

      if (!hasPseudoChanged && !hasEmailChanged && !hasPhoneChanged && !hasBirthDateChanged) {
        console.log('[Profile] No changes detected, skipping update');
        setIsUpdating(false);
        setShowProfileEditor(false);
        return;
      }

      console.log('[Profile] Changes detected:', {
        pseudo: hasPseudoChanged,
        email: hasEmailChanged,
        phone: hasPhoneChanged,
        birthDate: hasBirthDateChanged
      });

      // Check if pseudo is being changed
      if (hasPseudoChanged && editingProfile.pseudo) {
        // Validate pseudo content and format
        const validation = validatePseudo(editingProfile.pseudo);
        if (!validation.isValid) {
          setIsUpdating(false);
          setTimeout(() => {
            let errorMessage = t('pseudoMaxLength');
            if (validation.error === 'Pseudo contains inappropriate content') {
              errorMessage = t('pseudoInvalid');
            } else if (validation.error === 'Pseudo contains invalid characters') {
              errorMessage = t('pseudoInvalidCharacters');
            } else if (validation.error === 'Pseudo must be 16 characters or less') {
              errorMessage = t('pseudoMaxLength');
            }
            Alert.alert(t('error'), errorMessage);
          }, 300);
          return;
        }

        // Validate pseudo content with OpenAI
        try {
          const validationResponse = await fetch(
            `${getSupabaseUrl()}/functions/v1/valid-pseudo`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${getSupabaseAnonKey()}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ pseudo: editingProfile.pseudo.trim() }),
            }
          );

          const validationResult = await validationResponse.json();

          if (!validationResult.isValid) {
            setIsUpdating(false);
            setTimeout(() => {
              Alert.alert(t('error'), t('pseudoNotAccepted'));
            }, 300);
            return;
          }
        } catch (error) {
          console.error('Error validating pseudo with OpenAI:', error);
          // Continue with save if validation service fails
        }

        // Check if pseudo is already taken
        const { data: existingPseudoUser, error: pseudoCheckError } = await supabase
          .from('users')
          .select('smartuser_id')
          .eq('pseudo', editingProfile.pseudo.trim())
          .neq('smartuser_id', authState.user.smartuserId)
          .maybeSingle();

        if (pseudoCheckError) {
          console.error('Error checking pseudo:', pseudoCheckError);
        } else if (existingPseudoUser) {
          setIsUpdating(false);
          setTimeout(() => {
            Alert.alert(t('error'), t('pseudoTaken'));
          }, 300);
          return;
        }
      }

      // Check if email is being added/changed
      if (editingProfile.email && editingProfile.email !== previousProfile.email) {
        // Validate email format
        const emailValidation = validateEmail(editingProfile.email);
        if (!emailValidation.isValid) {
          setIsUpdating(false);
          setEmailError(t('invalidEmailFormat'));
          return;
        }

        const { data: existingEmailUser, error: emailCheckError } = await supabase
          .from('users')
          .select('smartuser_id')
          .eq('email', editingProfile.email.trim())
          .neq('smartuser_id', authState.user.smartuserId)
          .maybeSingle();

        if (emailCheckError) {
          console.error('Error checking email:', emailCheckError);
        } else if (existingEmailUser) {
          setIsUpdating(false);
          setEmailError(t('emailAlreadyExists'));
          return;
        }
      }

      // Check if phone number is being added/changed
      if (editingProfile.phoneNumber && editingProfile.phoneNumber !== previousProfile.phoneNumber) {
        // Validate phone number format
        const phoneValidation = validatePhoneNumber(editingProfile.phoneNumber);
        if (!phoneValidation.isValid) {
          setIsUpdating(false);
          setPhoneError(t('invalidPhoneFormat'));
          return;
        }

        const { data: existingPhoneUser, error: phoneCheckError } = await supabase
          .from('users')
          .select('smartuser_id')
          .eq('phone_number', editingProfile.phoneNumber.trim())
          .neq('smartuser_id', authState.user.smartuserId)
          .maybeSingle();

        if (phoneCheckError) {
          console.error('Error checking phone:', phoneCheckError);
        } else if (existingPhoneUser) {
          setIsUpdating(false);
          setPhoneError(t('phoneAlreadyExists'));
          return;
        }
      }

      const { error } = await supabase
        .from('users')
        .update({
          pseudo: editingProfile.pseudo || null,
          email: editingProfile.email || null,
          phone_number: editingProfile.phoneNumber || null,
          date_of_birth: editingProfile.dateOfBirth || null,
          updated_at: new Date().toISOString(),
        })
        .eq('smartuser_id', authState.user.smartuserId);

      if (error) {
        console.error('Error updating user profile:', error);
        Alert.alert(t('error'), 'Failed to update profile');
        return;
      }

      // Update local state first
      setUserProfile(editingProfile);
      setShowProfileEditor(false);

      // Check for gamification events
      const emailProvided = !previousProfile.email && editingProfile.email.trim();
      const phoneProvided = !previousProfile.phoneNumber && editingProfile.phoneNumber.trim();
      const birthDateProvided = !previousProfile.dateOfBirth && editingProfile.dateOfBirth.trim();

      // Check if user provided a custom pseudo (not default format)
      // Default formats: user{shortId} (email/phone), google{id} (Google), apple{id} (Apple)
      // Empty/null pseudo is also considered "default" (user never set one)
      const isDefaultPseudo = (pseudo: string | null | undefined) => {
        if (!pseudo || pseudo.trim() === '') return true;
        return pseudo.startsWith('user') || pseudo.startsWith('google') || pseudo.startsWith('apple');
      };
      const previousPseudoWasDefault = isDefaultPseudo(previousProfile.pseudo);
      const newPseudoIsCustom = editingProfile.pseudo && !isDefaultPseudo(editingProfile.pseudo);
      const pseudoProvided = hasPseudoChanged && previousPseudoWasDefault && newPseudoIsCustom;

      console.log('Gamification checks:', {
        emailProvided,
        phoneProvided,
        birthDateProvided,
        pseudoProvided,
        previousEmail: previousProfile.email,
        newEmail: editingProfile.email,
        previousPhone: previousProfile.phoneNumber,
        newPhone: editingProfile.phoneNumber,
        previousBirthDate: previousProfile.dateOfBirth,
        newBirthDate: editingProfile.dateOfBirth,
        previousPseudo: previousProfile.pseudo,
        newPseudo: editingProfile.pseudo
      });

      // Process individual field events first
      if (emailProvided) {
        console.log('Processing email_provided event');
        await processEvent('email_provided', {
          email: editingProfile.email,
          providedAt: new Date().toISOString()
        });
      }

      if (phoneProvided) {
        console.log('Processing msisdn_provided event');
        await processEvent('msisdn_provided', {
          phoneNumber: editingProfile.phoneNumber,
          providedAt: new Date().toISOString()
        });
      }

      if (birthDateProvided) {
        console.log('Processing birth_date_provided event');
        await processEvent('birth_date_provided', {
          dateOfBirth: editingProfile.dateOfBirth,
          providedAt: new Date().toISOString()
        });
      }

      if (pseudoProvided) {
        console.log('Processing pseudo_provided event');
        await processEvent('pseudo_provided', {
          pseudo: editingProfile.pseudo,
          providedAt: new Date().toISOString()
        });
      }

      // Check if profile became complete with this update
      // Profile is complete only when ALL 3 fields are filled: email, phone number, AND date of birth
      const profileBecameComplete = editingProfile.email.trim() &&
                                   editingProfile.phoneNumber.trim() &&
                                   editingProfile.dateOfBirth.trim() &&
                                   (emailProvided || phoneProvided || birthDateProvided);

      if (profileBecameComplete) {
        console.log('Processing complete_profile event');
        await processEvent('complete_profile', {
          email: editingProfile.email,
          phoneNumber: editingProfile.phoneNumber,
          dateOfBirth: editingProfile.dateOfBirth,
          completedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      Alert.alert(t('error'), 'Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleBiometric = async () => {
    console.log('🔐 handleToggleBiometric called with state:', {
      platform: Platform.OS,
      biometricSupportAvailable: biometricSupport.isAvailable,
      biometricEnabled,
      hasUser: !!authState.user,
      hasToken: !!authState.token,
      supportedTypes: biometricSupport.supportedTypes
    });

    if (!biometricSupport.isAvailable) {
      console.log('🔐 Biometric not available, showing alert');
     Alert.alert(t('error'), t('biometricNotAvailable'));
      return;
    }

    if (biometricEnabled) {
      console.log('🔐 Disabling biometric login...');
      // Disable biometric login
      const success = await disableBiometricLogin();
      console.log('🔐 Disable biometric result:', success);
      if (success) {
        setBiometricEnabled(false);
       const successMessage = Platform.OS === 'web'
         ? t('webauthnLoginDisabled')
         : Platform.OS === 'android'
           ? t('androidBiometricLoginDisabled')
           : biometricSupport.supportedTypes.includes('Face ID')
             ? t('faceIdLoginDisabled')
             : biometricSupport.supportedTypes.includes('Touch ID')
               ? t('touchIdLoginDisabled')
               : t('biometricLoginDisabled');
       Alert.alert(t('success'), successMessage);
      } else {
       const errorMessage = Platform.OS === 'web' 
         ? t('failedToDisableWebauthn')
         : t('failedToDisableBiometric');
       Alert.alert(t('error'), errorMessage);
      }
    } else {
      console.log('🔐 Enabling biometric login...');
      // Enable biometric login
      const success = Platform.OS === 'web' 
        ? await enableBiometricLogin(authState.user, authState.token)
        : await enableBiometricLogin();
      console.log('🔐 Enable biometric result:', success);
      if (success) {
        setBiometricEnabled(true);
       const successMessage = Platform.OS === 'web'
         ? t('webauthnLoginEnabled')
         : Platform.OS === 'android'
           ? t('androidBiometricLoginEnabled')
           : biometricSupport.supportedTypes.includes('Face ID')
             ? t('faceIdLoginEnabled')
             : biometricSupport.supportedTypes.includes('Touch ID')
               ? t('touchIdLoginEnabled')
               : t('biometricLoginEnabled');
       Alert.alert(t('success'), successMessage);
      } else {
       const errorMessage = Platform.OS === 'web' 
         ? t('failedToEnableWebauthn')
         : t('failedToEnableBiometric');
       Alert.alert(t('error'), errorMessage);
      }
    }
  };

  const handleContactUs = async () => {
    const email = Platform.OS === 'web' ? 'support-ma@mes-abonnements.ma' : 'customer@hekaya.ma';
    const subject = encodeURIComponent('Contact');
    const mailtoUrl = `mailto:${email}?subject=${subject}`;

    try {
      if (Platform.OS === 'android') {
        await Linking.openURL(mailtoUrl);
      } else {
        const canOpen = await Linking.canOpenURL(mailtoUrl);
        if (canOpen) {
          await Linking.openURL(mailtoUrl);
        } else {
          Alert.alert(
            t('error'),
            t('noEmailApp'),
            [{ text: t('ok') }]
          );
        }
      }
    } catch (error) {
      console.error('[Profile] Error opening email app:', error);
      Alert.alert(
        t('error'),
        t('errorOpeningEmail'),
        [{ text: t('ok') }]
      );
    }
  };

  const menuItems = [
    { icon: Globe, title: t('language'), onPress: () => setShowLanguageSelector(true) },
    ...(Platform.OS !== 'web' ? [{ icon: Bell, title: t('notifications'), onPress: () => setShowNotificationSettings(true) }] : []),
    { icon: MessageCircle, title: t('contactUs'), onPress: handleContactUs },
  ];

  const cookiesMenuItems = [
    { icon: Cookie, title: t('cookies'), onPress: () => router.push('/legal-doc?slug=cookies') },
    { icon: Cookie, title: t('cookieSettings'), onPress: () => {
      console.log('[Profile] Cookie settings clicked, calling showPreferences()');
      showPreferences();
    }},
  ];

  const legalMenuItems = [
    { icon: Shield, title: t('privacyPolicy'), onPress: () => router.push('/legal-doc?slug=politique-de-confidentialite') },
    { icon: FileText, title: t('termsConditions'), onPress: () => router.push('/legal-doc?slug=conditions-generales-dutilisation-ios') },
    { icon: Scale, title: t('legalNotice'), onPress: () => router.push('/legal-doc?slug=mentions-legales') },
  ];

  const handleSignIn = () => {
    showSignInModal();
  };

  const cleanOperationIdFromUrl = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('operationId');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  };

  const handleProfileSignInSuccess = () => {
    if (hasOperationId) {
      dcbFlowCompletedRef.current = true;
      cleanOperationIdFromUrl();
    }
    router.replace('/(tabs)');
  };

  const handleDCBComplete = () => {
    dcbFlowCompletedRef.current = true;
    cleanOperationIdFromUrl();
    setShowDCBFlow(false);
    setDcbFlowData(null);
    router.replace('/(tabs)');
  };

  const handleDcbUnsubscribe = async () => {
    setShowDcbUnsubModal(false);
    setIsDcbUnsubscribing(true);

    try {
      const apiUrl = `${getSupabaseUrl()}/functions/v1/dcb-cancel`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getSupabaseAnonKey()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ smartuserId: authState.user?.smartuserId }),
      });

      const data = await response.json();

      if (data.success && data.isUnsubscribed) {
        if (data.gracePeriod) {
          await checkSubscriptionStatus();
        } else {
          updateUserSubscription(false);
          updateUserPaymentMethod(null);
        }
        setDcbUnsubResult('success');
      } else {
        setDcbUnsubResult('error');
      }
    } catch {
      setDcbUnsubResult('error');
    } finally {
      setIsDcbUnsubscribing(false);
    }
  };

  // DCB subscribers: fetch start date, next payment date and billing period
  // (deduced server-side from the payment dates) for the profile display.
  useEffect(() => {
    const smartuserId = authState.user?.smartuserId;
    const isDcbUser = authState.user?.paymentMethod === 'dcb';
    const userIsSubscribed = isSubscribed || authState.user?.isSubscribed === true;

    if (!isDcbUser || !userIsSubscribed || !smartuserId) {
      setDcbSubscriptionInfo(null);
      return;
    }

    let cancelled = false;
    fetchDcbSubscriptionInfo(smartuserId).then((info) => {
      if (!cancelled && info) {
        setDcbSubscriptionInfo(info);
      }
    });
    return () => { cancelled = true; };
  }, [authState.user?.smartuserId, authState.user?.paymentMethod, authState.user?.isSubscribed, isSubscribed]);

  const formatDcbDate = (iso: string): string => {
    const localeMap: Record<string, string> = {
      en: 'en-US', fr: 'fr-FR', es: 'es-ES', it: 'it-IT', de: 'de-DE', ro: 'ro-RO', ar: 'ar', am: 'am',
    };
    const locale = localeMap[language] || 'en-US';
    const date = new Date(iso);
    try {
      return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(date);
    } catch {
      return date.toLocaleDateString(locale);
    }
  };

  const dcbOfferLabelKey =
    dcbSubscriptionInfo?.period === 'daily' ? 'dcbOfferDaily'
    : dcbSubscriptionInfo?.period === 'weekly' ? 'dcbOfferWeekly'
    : dcbSubscriptionInfo?.period === 'monthly' ? 'dcbOfferMonthly'
    : dcbSubscriptionInfo?.period === 'quarterly' ? 'dcbOfferQuarterly'
    : dcbSubscriptionInfo?.period === 'yearly' ? 'dcbOfferYearly'
    : 'dcbOfferRecurring';

  const handleUnsubscribePress = () => {
    const isDcbUser = authState.user?.paymentMethod === 'dcb';
    const userIsCurrentlySubscribed = isSubscribed || authState.user?.isSubscribed === true;

    if (isDcbUser && userIsCurrentlySubscribed) {
      setShowDcbUnsubModal(true);
    } else {
      router.push('/legal-doc?slug=se-desabonner');
    }
  };

  const handleLogout = async () => {
    await logout();
    router.replace('/(tabs)');
  };

  const handleDeleteAccount = async () => {
    if (!authState.user?.smartuserId) return;

    setIsDeletingAccount(true);
    try {
      const apiUrl = getApiUrl('/delete-account');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getSupabaseAnonKey()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          smartuserId: authState.user.smartuserId,
        }),
      });

      if (!response.ok) {
        if (response.status === 409) {
          try {
            const errorData = await response.json();
            if (errorData.error === 'USER_HAS_ACTIVE_SUBSCRIPTIONS') {
              setShowDeleteAccountModal(false);
              setShowActiveSubErrorModal(true);
              return;
            }
            if (errorData.error === 'USER_IN_GRACE_PERIOD') {
              setShowDeleteAccountModal(false);
              setShowGracePeriodModal(true);
              return;
            }
          } catch {}
        }
        Alert.alert(t('error'), t('accountDeleteError'));
        return;
      }

      setShowDeleteAccountModal(false);
      Alert.alert(t('accountDeleted'), t('accountDeletedSuccess'));
      await logout();
      router.replace('/(tabs)');
    } catch (error) {
      console.error('Error deleting account:', error);
      Alert.alert(t('error'), t('accountDeleteError'));
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const loadAsyncStorageStats = async () => {
    try {
      console.log('[AsyncStorage Stats] Loading enhanced stats...');

      // Read from the actual cache keys
      const contentCacheData = await AsyncStorage.getItem('@content_cache');
      const episodesCacheIndex = await AsyncStorage.getItem('@episodes_cache_index');
      const episodesCacheStats = await AsyncStorage.getItem('@episodes_cache_stats');
      const unifiedStartupCacheData = await AsyncStorage.getItem('@unified_startup_cache_v1');

      let rubriquesCount = 0;
      let seriesCount = 0;
      let cachedSeriesCount = 0;
      let totalCachedEpisodes = 0;
      const cachedSeriesList: Array<{
        seriesId: string;
        episodeCount: number;
        cacheAge: string;
        timestamp: number;
      }> = [];

      // Parse content cache to get rubriques and series counts
      if (contentCacheData) {
        const contentCache = JSON.parse(contentCacheData);

        // Count rubriques from all campaigns
        if (contentCache.rubriques) {
          rubriquesCount = Object.values(contentCache.rubriques).reduce(
            (total: number, rubriqueArray: any) => total + (Array.isArray(rubriqueArray) ? rubriqueArray.length : 0),
            0
          );
        }

        // Count series from all campaigns
        if (contentCache.series) {
          seriesCount = Object.values(contentCache.series).reduce(
            (total: number, seriesArray: any) => total + (Array.isArray(seriesArray) ? seriesArray.length : 0),
            0
          );
        }

        console.log('[AsyncStorage Stats] Content Cache:', {
          rubriques: rubriquesCount,
          series: seriesCount,
          campaignId: contentCache.campaignId,
          age: contentCache.timestamp ? `${Math.round((Date.now() - contentCache.timestamp) / 1000 / 60)}min` : 'unknown'
        });
      }

      // Read each episode cache entry and count the actual episodes
      if (episodesCacheIndex) {
        const episodesIndex = JSON.parse(episodesCacheIndex);

        if (Array.isArray(episodesIndex)) {
          cachedSeriesCount = episodesIndex.length;
          console.log(`[AsyncStorage Stats] Found ${cachedSeriesCount} cache keys (series with cached episodes)`);

          // Loop through each cache key and count episodes
          for (let i = 0; i < episodesIndex.length; i++) {
            const cacheKey = episodesIndex[i];
            try {
              const cachedData = await AsyncStorage.getItem(cacheKey);
              if (cachedData) {
                const parsed = JSON.parse(cachedData);
                const episodeCount = Array.isArray(parsed.data) ? parsed.data.length : 0;
                totalCachedEpisodes += episodeCount;

                // Extract series ID from cache key (@episodes_cache_<campaignId>_<seriesId>)
                const parts = cacheKey.split('_');
                const seriesId = parts[parts.length - 1];
                const timestamp = parsed.timestamp || 0;
                const age = timestamp ? Date.now() - timestamp : 0;
                const ageMinutes = Math.round(age / 60000);
                const ageHours = Math.round(age / 3600000);
                const cacheAge = ageHours > 0 ? `${ageHours}h ago` : `${ageMinutes}m ago`;

                cachedSeriesList.push({
                  seriesId,
                  episodeCount,
                  cacheAge,
                  timestamp,
                });

                if (i < 3) { // Log first 3 for debugging
                  console.log(`[AsyncStorage Stats] [${i + 1}/${cachedSeriesCount}] ${cacheKey}: ${episodeCount} episodes (${cacheAge})`);
                }
              }
            } catch (parseError) {
              console.error(`[AsyncStorage Stats] Error parsing cache key ${cacheKey}:`, parseError);
            }
          }

          console.log(`[AsyncStorage Stats] Total episodes counted: ${totalCachedEpisodes} across ${cachedSeriesCount} series`);
        }
      }

      // Sort by timestamp (newest first)
      cachedSeriesList.sort((a, b) => b.timestamp - a.timestamp);

      const avgEpisodesPerSeries = cachedSeriesCount > 0
        ? Math.round(totalCachedEpisodes / cachedSeriesCount)
        : 0;

      // Extract URLs from first 10 episodes of series 9619162 for debugging
      const episodeUrls: Array<{
        seriesId: string;
        episodeId: string;
        episodeTitle: string;
        hasVideoUrl: boolean;
        hasDrmUrl: boolean;
        videoUrl: string | null;
        drmUrl: string | null;
      }> = [];

      if (episodesCacheIndex) {
        const episodesIndex = JSON.parse(episodesCacheIndex);

        if (Array.isArray(episodesIndex)) {
          // Find the cache key for series 9619162
          const targetSeriesId = '9619162';
          const targetCacheKey = episodesIndex.find((key: string) => key.endsWith(`_${targetSeriesId}`));

          if (targetCacheKey) {
            try {
              const cachedData = await AsyncStorage.getItem(targetCacheKey);
              if (cachedData) {
                const parsed = JSON.parse(cachedData);
                if (Array.isArray(parsed.data)) {
                  // Extract series ID from cache key
                  const parts = targetCacheKey.split('_');
                  const seriesId = parts[parts.length - 1];

                  // Take first 10 episodes of this series
                  const episodesToShow = parsed.data.slice(0, 10);

                  episodesToShow.forEach((episode: any) => {
                    const hasVideoUrl = !!(episode.video_url || episode.url_streaming_no_drm);
                    const hasDrmUrl = !!episode.url_streaming_drm;

                    episodeUrls.push({
                      seriesId,
                      episodeId: episode.episode_id?.toString() || episode.id?.toString() || 'unknown',
                      episodeTitle: episode.title || `Episode ${episode.episode_position || '?'}`,
                      hasVideoUrl,
                      hasDrmUrl,
                      videoUrl: episode.video_url || episode.url_streaming_no_drm || null,
                      drmUrl: episode.url_streaming_drm || null,
                    });
                  });

                  console.log(`[AsyncStorage Stats] Extracted URLs for ${episodeUrls.length} episodes from series ${targetSeriesId}`);
                } else {
                  console.log(`[AsyncStorage Stats] No episode data found for series ${targetSeriesId}`);
                }
              } else {
                console.log(`[AsyncStorage Stats] No cached data found for series ${targetSeriesId}`);
              }
            } catch (parseError) {
              console.error(`[AsyncStorage Stats] Error parsing cache key ${targetCacheKey}:`, parseError);
            }
          } else {
            console.log(`[AsyncStorage Stats] Series ${targetSeriesId} not found in cache`);
          }
        }
      }

      // Parse cache stats
      let cacheStatsData = null;
      if (episodesCacheStats) {
        try {
          const stats = JSON.parse(episodesCacheStats);
          const totalRequests = (stats.cacheHits || 0) + (stats.cacheMisses || 0);
          const hitRate = totalRequests > 0 ? Math.round((stats.cacheHits / totalRequests) * 100) : 0;
          cacheStatsData = {
            cacheHits: stats.cacheHits || 0,
            cacheMisses: stats.cacheMisses || 0,
            apiCalls: stats.apiCalls || 0,
            hitRate,
          };
        } catch (e) {
          console.error('[AsyncStorage Stats] Error parsing cache stats:', e);
        }
      }

      // Parse unified startup cache for prefetch info
      let lastFullPrefetchTimestamp = null;
      let lastFullPrefetchCampaignId = null;
      let lastFullPrefetchAge = null;
      let prefetchMode: 'full' | 'incremental' | 'none' = 'none';

      if (unifiedStartupCacheData) {
        try {
          const startupCache = JSON.parse(unifiedStartupCacheData);
          lastFullPrefetchTimestamp = startupCache.lastFullPrefetchTimestamp || null;
          lastFullPrefetchCampaignId = startupCache.lastFullPrefetchCampaignId || null;

          if (lastFullPrefetchTimestamp && lastFullPrefetchTimestamp > 0) {
            const age = Date.now() - lastFullPrefetchTimestamp;
            const ageHours = Math.round(age / 3600000);
            const ageMinutes = Math.round(age / 60000);
            lastFullPrefetchAge = ageHours > 0 ? `${ageHours}h ago` : `${ageMinutes}m ago`;

            // Determine if it would need full prefetch (>24h)
            if (age > 24 * 60 * 60 * 1000) {
              prefetchMode = 'full';
            } else {
              prefetchMode = 'incremental';
            }
          } else {
            prefetchMode = 'full'; // Never ran before
            lastFullPrefetchAge = 'Never';
          }
        } catch (e) {
          console.error('[AsyncStorage Stats] Error parsing startup cache:', e);
        }
      }

      // Get prefetch stats from coordinator (dynamic import to avoid circular deps)
      let prefetchStats = null;
      let prefetchInProgress = false;
      try {
        const module = await import('@/services/prefetchOrchestrator');
        const coordinator = module?.contentPrefetchCoordinator;
        if (coordinator && typeof coordinator.getPrefetchStats === 'function') {
          prefetchStats = coordinator.getPrefetchStats();
          prefetchInProgress = coordinator.isPrefetchInProgress();
        }
      } catch (e) {
        console.error('[AsyncStorage Stats] Error getting prefetch stats:', e);
      }

      console.log('[AsyncStorage Stats] Final counts:', {
        rubriques: rubriquesCount,
        series: seriesCount,
        cachedSeriesWithEpisodes: cachedSeriesCount,
        totalCachedEpisodes: totalCachedEpisodes,
        avgEpisodesPerSeries: avgEpisodesPerSeries,
        lastFullPrefetchAge,
        prefetchMode,
        cacheStatsData,
      });

      setAsyncStorageStats({
        rubriquesCount,
        seriesCount,
        freeEpisodesCount,
        episodesCount: cachedSeriesCount,
        cachedSeriesCount,
        totalCachedEpisodes,
        avgEpisodesPerSeries,
        lastFullPrefetchTimestamp,
        lastFullPrefetchCampaignId,
        lastFullPrefetchAge,
        prefetchMode,
        prefetchInProgress,
        prefetchStats,
        cacheStatsData,
        cachedSeriesList,
        episodeUrls,
      });

      await loadRecentAchievements();
    } catch (error) {
      console.error('[AsyncStorage Stats] ❌ Error loading stats:', error);
      setAsyncStorageStats({
        rubriquesCount: 0,
        seriesCount: 0,
        freeEpisodesCount: 0,
        episodesCount: 0,
        cachedSeriesCount: 0,
        totalCachedEpisodes: 0,
        avgEpisodesPerSeries: 0,
        lastFullPrefetchTimestamp: null,
        lastFullPrefetchCampaignId: null,
        lastFullPrefetchAge: null,
        prefetchMode: 'none',
        prefetchInProgress: false,
        prefetchStats: null,
        cacheStatsData: null,
        cachedSeriesList: [],
        episodeUrls: [],
      });
    }
  };

  const loadRecentAchievements = async () => {
    if (!authState.user?.smartuserId) {
      setRecentAchievements([]);
      return;
    }

    try {
      console.log('[Debug] Loading recent achievements for user:', authState.user.smartuserId);
      const { data, error } = await supabase
        .from('user_achievements')
        .select('id, achievement_type, coins_earned, created_at, metadata')
        .eq('smartuser_id', authState.user.smartuserId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('[Debug] Error loading achievements:', error);
        setRecentAchievements([]);
      } else {
        console.log('[Debug] Loaded achievements:', data?.length || 0);
        setRecentAchievements(data || []);
      }
    } catch (err) {
      console.error('[Debug] Exception loading achievements:', err);
      setRecentAchievements([]);
    }
  };

  const handleTriggerFullPrefetch = async () => {
    const title = 'Force Full Prefetch';
    const message = 'This will manually trigger a full prefetch of all series episodes. This may take several minutes. Continue?';

    const doTrigger = async () => {
      try {
        console.log('[Debug] Triggering full prefetch...');

        const { unifiedStartupCache } = await import('@/services/unifiedStartupCache');
        const { fullPrefetchOrchestrator } = await import('@/services/prefetchOrchestrator');
        const { useCampaignConfig } = await import('@/hooks/useCampaignConfig');
        const { useContent } = await import('@/hooks/useContent');

        if (!campaignCountriesLanguagesId) {
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.alert('No campaign ID available. Please wait for app to load.');
          } else {
            Alert.alert('Error', 'No campaign ID available. Please wait for app to load.');
          }
          return;
        }

        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.alert('Full prefetch started in background. Check console logs for progress.');
        } else {
          Alert.alert('Info', 'Full prefetch started in background. Check console logs for progress.');
        }

        await unifiedStartupCache.markFullPrefetchComplete('force-reset-' + Date.now());

        setTimeout(async () => {
          await loadAsyncStorageStats();
        }, 2000);

      } catch (error) {
        console.error('[Debug] Error triggering full prefetch:', error);
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.alert('Failed to trigger full prefetch');
        } else {
          Alert.alert('Error', 'Failed to trigger full prefetch');
        }
      }
    };

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      if (window.confirm(`${title}\n\n${message}`)) {
        await doTrigger();
      }
    } else {
      Alert.alert(title, message, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Start Prefetch', onPress: doTrigger },
      ]);
    }
  };

  const handleClearEpisodesCache = async () => {
    const title = 'Clear Episodes Cache';
    const message = 'This will clear only the episodes cache (keep content/series data). Are you sure?';

    const doClear = async () => {
      try {
        console.log('[Debug] Clearing episodes cache...');
        const { episodesCacheService } = await import('@/services/episodesCacheService');
        const { unifiedStartupCache } = await import('@/services/unifiedStartupCache');
        await episodesCacheService.clear();
        await unifiedStartupCache.clear();
        console.log('[Debug] Episodes cache and MMKV cache cleared');
        await refetchCampaign();
        console.log('[Debug] Campaign config reloaded from database');
        await loadAsyncStorageStats();

        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.alert('Episodes cache cleared and campaign config refreshed. The page will reload.');
          window.location.reload();
        } else {
          Alert.alert('Success', 'Episodes cache cleared and campaign config refreshed.');
        }
      } catch (error) {
        console.error('[Debug] Error clearing episodes cache:', error);
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.alert('Failed to clear episodes cache');
        } else {
          Alert.alert('Error', 'Failed to clear episodes cache');
        }
      }
    };

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      if (window.confirm(`${title}\n\n${message}`)) {
        await doClear();
      }
    } else {
      Alert.alert(title, message, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Clear', style: 'destructive', onPress: doClear },
      ]);
    }
  };

  const handleClearAsyncStorage = async () => {
    const title = 'Clear ALL Caches';
    const message = 'This will clear ALL app data (AsyncStorage, localStorage, in-memory caches). You will need to restart and log in again. Are you sure?';

    const doClear = async () => {
      try {
        console.log('[ClearAll] Clearing all caches...');

        const { unifiedStartupCache } = await import('@/services/unifiedStartupCache');
        const { persistentCountryCache } = await import('@/services/persistentCountryCacheService');
        const { persistentContentCache } = await import('@/services/persistentContentCacheService');
        const { persistentCampaignCache } = await import('@/services/persistentCampaignCacheService');
        const { contentCacheService } = await import('@/services/contentCacheService');
        const { episodesCacheService } = await import('@/services/episodesCacheService');
        const { episodeDetailsCacheService } = await import('@/services/episodeDetailsCacheService');
        const { favoritesCacheService } = await import('@/services/favoritesCacheService');
        const { seriesMetadataCacheService } = await import('@/services/seriesMetadataCacheService');
        const { userProfileCacheService } = await import('@/services/userProfileCacheService');
        const { campaignCache } = await import('@/services/campaignCacheService');
        const { languageCacheService } = await import('@/services/languageCacheService');
        const { clearCountryCache } = await import('@/lib/countryDetection');

        await AsyncStorage.clear();
        console.log('[ClearAll] AsyncStorage cleared');

        await unifiedStartupCache.clear();
        console.log('[ClearAll] Unified startup cache cleared');

        await persistentCountryCache.clear();
        await persistentContentCache.clear();
        await persistentCampaignCache.clear();
        console.log('[ClearAll] Persistent caches cleared (country, content, campaign)');

        await contentCacheService.clear();
        await episodesCacheService.clear();
        await episodeDetailsCacheService.clear();
        await favoritesCacheService.clear();
        await seriesMetadataCacheService.clear();
        console.log('[ClearAll] Content/episodes/favorites/metadata caches cleared');

        userProfileCacheService.clearAll();
        campaignCache.clear();
        languageCacheService.clearCache();
        clearCountryCache();
        console.log('[ClearAll] In-memory caches cleared (profile, campaign, language, country)');

        if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.clear();
          console.log('[ClearAll] localStorage cleared');
        }

        await refetchCampaign();
        console.log('[ClearAll] Campaign config reloaded from database');
        await loadAsyncStorageStats();

        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.alert('All caches cleared successfully. The page will reload.');
          window.location.reload();
        } else {
          Alert.alert('Success', 'All caches cleared (AsyncStorage + localStorage + in-memory). Please restart the app.');
        }
      } catch (error) {
        console.error('[ClearAll] Error clearing data:', error);
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.alert('Failed to clear all caches');
        } else {
          Alert.alert('Error', 'Failed to clear all caches');
        }
      }
    };

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      if (window.confirm(`${title}\n\n${message}`)) {
        await doClear();
      }
    } else {
      Alert.alert(title, message, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Clear Everything', style: 'destructive', onPress: doClear },
      ]);
    }
  };

  const testDcbCookie = async () => {
    setDcbCookieLoading(true);
    try {
      const apiUrl = `${getSupabaseUrl()}/functions/v1/manage-users`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getSupabaseAnonKey()}`,
          'Apikey': getSupabaseAnonKey(),
        },
        body: JSON.stringify({ action: 'dcb_debug_cookie' }),
      });
      const data = await response.json();
      const hostname = Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.hostname : 'N/A (native)';
      setDcbCookieDebug({ ...data, clientHostname: hostname, supabaseUrl: getSupabaseUrl() });
    } catch (err) {
      setDcbCookieDebug({ error: (err as Error)?.message || 'Unknown error' });
    } finally {
      setDcbCookieLoading(false);
    }
  };

  const handleRefreshCampaignConfig = async () => {
    try {
      console.log('[Debug] Refreshing campaign config...');
      const { unifiedStartupCache } = await import('@/services/unifiedStartupCache');
      await unifiedStartupCache.clear();
      console.log('[Debug] ✅ MMKV cache cleared');
      await refetchCampaign();
      console.log('[Debug] ✅ Campaign config reloaded from database');
      await loadAsyncStorageStats();
      Alert.alert('Success', 'Campaign config refreshed from database.');
    } catch (error) {
      console.error('[Debug] ❌ Error refreshing campaign config:', error);
      Alert.alert('Error', 'Failed to refresh campaign config');
    }
  };

  const handleSelectImageSource = () => {
    setShowImagePickerModal(true);
  };

  const handlePickFromLibrary = async () => {
    setShowImagePickerModal(false);
    try {
      const image = await pickImageFromLibrary();
      if (image) {
        await handleImageUpload(image.uri);
      }
    } catch (error) {
      console.error('[Profile Picture] Error picking from library:', error);
      Alert.alert(t('error'), t('failedToPickImage'));
    }
  };

  const handleTakePhoto = async () => {
    setShowImagePickerModal(false);
    try {
      const image = await takePhotoWithCamera();
      if (image) {
        await handleImageUpload(image.uri);
      }
    } catch (error) {
      console.error('[Profile Picture] Error taking photo:', error);
      Alert.alert(t('error'), t('failedToTakePhoto'));
    }
  };

  const handleImageUpload = async (imageUri: string) => {
    if (!authState.user?.smartuserId) return;

    setUploadImageError(null);
    setIsUploadingImage(true);
    try {
      const result = await uploadProfilePicture(
        authState.user.smartuserId.toString(),
        imageUri,
        authState.token || undefined
      );

      if (!result.success) {
        let errorMessage = t('failedToUploadImage');
        if (result.error?.includes('inappropriate content')) {
          errorMessage = t('imageInappropriateContent');
        } else if (result.error) {
          errorMessage = result.error;
        }
        setUploadImageError(errorMessage);
        Alert.alert(t('error'), errorMessage);
        return;
      }

      setUserProfile(prev => ({ ...prev, profilePictureUrl: result.url! }));
      setEditingProfile(prev => ({ ...prev, profilePictureUrl: result.url! }));
      Alert.alert(t('success'), t('profilePictureUpdated'));
    } catch (error) {
      console.error('[Profile Picture] Error uploading image:', error);
      setUploadImageError(t('failedToUploadImage'));
      Alert.alert(t('error'), t('failedToUploadImage'));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!authState.user?.smartuserId) return;

    Alert.alert(
      t('removeProfilePicture'),
      t('removeProfilePictureConfirm'),
      [
        {
          text: t('cancel'),
          style: 'cancel',
        },
        {
          text: t('remove'),
          style: 'destructive',
          onPress: async () => {
            try {
              const success = await removeProfilePicture(authState.user.smartuserId);
              if (success) {
                setUserProfile(prev => ({ ...prev, profilePictureUrl: '' }));
                setEditingProfile(prev => ({ ...prev, profilePictureUrl: '' }));
                Alert.alert(t('success'), t('profilePictureRemoved'));
              } else {
                Alert.alert(t('error'), t('failedToRemoveProfilePicture'));
              }
            } catch (error) {
              console.error('[Profile Picture] Error removing:', error);
              Alert.alert(t('error'), t('failedToRemoveProfilePicture'));
            }
          },
        },
      ]
    );
  };

  if (Platform.OS === 'web' && playerIsVisible) {
    return null;
  }

  return (
    <LinearGradient
      colors={['#000000', '#A855F7']}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={{ flex: 1 }}
    >
    <SafeAreaView style={styles.container} edges={Platform.OS !== 'web' ? ['top', 'left', 'right'] : ['left', 'right']}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'padding'}
        keyboardVerticalOffset={0}
      >
      <View style={styles.desktopContainer}>
      <ScrollView
        ref={mainScrollViewRef}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: getContentBottomPadding(insets.bottom) }}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        removeClippedSubviews={Platform.OS === 'android'}
        overScrollMode="never"
        nestedScrollEnabled={true}
      >
        {Platform.OS !== 'web' && (
          <View style={styles.nativeHeader}>
            <TouchableOpacity onPress={() => router.push('/(tabs)')}>
              <RNImage source={require('@/assets/images/logo-hk.webp')} style={styles.nativeHeaderLogo} resizeMode="contain" />
            </TouchableOpacity>
          </View>
        )}
        {Platform.OS !== 'web' && (
        <View style={styles.headerUserInfo}>
          <View style={styles.headerAvatarContainer}>
            <View style={styles.headerAvatar}>
              {userProfile.profilePictureUrl ? (
                <RNImage
                  source={{ uri: userProfile.profilePictureUrl }}
                  style={styles.headerAvatarImage}
                  resizeMode="cover"
                />
              ) : (
                <User size={20} color="#666" />
              )}
              {isUploadingImage && (
                <View style={styles.headerAvatarLoadingOverlay}>
                  <ActivityIndicator size="small" color={primaryColor} />
                </View>
              )}
            </View>
            {authState.user && (
              <TouchableOpacity
                style={[styles.headerCameraButton, { backgroundColor: primaryColor }]}
                onPress={handleSelectImageSource}
                disabled={isUploadingImage}
              >
                <Camera size={10} color="#ffffff" />
              </TouchableOpacity>
            )}
          </View>
          <Text style={styles.headerUserName} numberOfLines={1}>
            {authState.user
              ? (userProfile.pseudo || authState.user.pseudo || generateDefaultPseudo(authState.user.smartuserId))
              : 'Guest'}
          </Text>
          {authState.user && (
            <TouchableOpacity
              onPress={() => {
                setEditingProfile(userProfile);
                setShowProfileEditor(true);
              }}
              style={styles.headerEditButton}
            >
              <Pencil size={14} color={primaryColor} />
            </TouchableOpacity>
          )}
        </View>
        )}

        {uploadImageError && (
          <View style={styles.uploadErrorContainer}>
            <Text style={styles.uploadErrorText}>{uploadImageError}</Text>
            <TouchableOpacity
              style={styles.uploadErrorDismissButton}
              onPress={() => setUploadImageError(null)}
            >
              <Text style={styles.uploadErrorDismissText}>OK</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* User Section */}
        <View style={styles.userSection}>
          {hasOperationId && dcbValidationStatus === 'validating' && (
            <View style={styles.dcbValidatingContainer}>
              <ActivityIndicator size="large" color={primaryColor} />
              <Text style={styles.dcbValidatingText}>{t('otp.dcbValidatingSubscription')}</Text>
            </View>
          )}

          {hasOperationId && dcbValidationStatus === 'invalid' && (
            <View style={styles.dcbValidatingContainer}>
              <Text style={styles.dcbErrorText}>{dcbValidationError || t('otp.dcbPaymentNotFound')}</Text>
            </View>
          )}

          {!authState.user && hasOperationId && autoSignupInProgress && (
            <View style={[styles.profileLoginFormContainer, isDesktopWeb && styles.profileLoginFormContainerDesktop, { alignItems: 'center' }]}>
              <ActivityIndicator size="large" color={primaryColor} />
              <Text style={{ color: '#aaa', textAlign: 'center', marginTop: 12 }}>
                {t('otp.subscriptionWelcomeMessage')}
              </Text>
            </View>
          )}

          {!authState.user && !showDCBFlow && !autoSignupInProgress && (!hasOperationId || dcbValidationStatus === 'valid') && (
            <View style={[styles.profileLoginFormContainer, isDesktopWeb && styles.profileLoginFormContainerDesktop]}>
              <OTPLoginFlow
                onSuccessCallback={handleProfileSignInSuccess}
                showSocialLogin={!hasOperationId}
                titleOverride={hasOperationId ? t('otp.subscriptionWelcomeTitle') : undefined}
                contextMessage={hasOperationId ? t('otp.subscriptionWelcomeMessage') : undefined}
                dcbContext={hasOperationId && dcbPrivilegedLoginData ? dcbPrivilegedLoginData : undefined}
              />
            </View>
          )}

          {showDCBFlow && dcbFlowData && operationId && (
            <DCBPaymentFlow
              operationId={operationId}
              isNewUser={dcbFlowData.isNewUser}
              identifier={dcbFlowData.identifier}
              identityType={dcbFlowData.identityType}
              sessionToken={dcbFlowData.sessionToken}
              smartuserId={dcbFlowData.smartuserId}
              supabaseUserId={dcbFlowData.supabaseUserId}
              onComplete={handleDCBComplete}
              dcbSessionToken={dcbPrivilegedLoginData?.dcbSessionToken}
              dcbAutologinToken={dcbPrivilegedLoginData?.dcbAutologinToken}
              dcbTemporaryAbsoluteReference={dcbPrivilegedLoginData?.dcbTemporaryAbsoluteReference}
              dcbIdentities={dcbPrivilegedLoginData?.identities}
            />
          )}

          {authState.user && paymentReturnStatus === 'verifying' && (
            <View style={[styles.paymentBanner, { borderColor: primaryColor }]}>
              <ActivityIndicator size="small" color="#ffffff" style={{ marginBottom: 8 }} />
              <Text style={styles.paymentBannerTitle}>{t('paymentVerifying')}</Text>
            </View>
          )}

          {authState.user && paymentReturnStatus === 'success' && (
            <View style={[styles.paymentBannerSuccess, { backgroundColor: primaryColor }]}>
              <Check size={24} color="#ffffff" style={{ marginBottom: 8 }} />
              <Text style={styles.paymentBannerTitle}>{t('paymentSuccessTitle')}</Text>
              <Text style={styles.paymentBannerMessage}>{t('paymentSuccessMessage')}</Text>
              <TouchableOpacity
                style={styles.paymentBannerButton}
                onPress={() => {
                  setPaymentReturnStatus('idle');
                  router.replace('/(tabs)');
                }}
              >
                <Text style={styles.paymentBannerButtonText}>{t('paymentSuccessButton')}</Text>
              </TouchableOpacity>
            </View>
          )}

          {authState.user && paymentReturnStatus === 'error' && (
            <View style={[styles.paymentBanner, { borderColor: primaryColor }]}>
              <Text style={styles.paymentBannerTitle}>{t('paymentPendingMessage')}</Text>
              <TouchableOpacity
                style={styles.paymentBannerButton}
                onPress={async () => {
                  setPaymentReturnStatus('verifying');
                  const confirmed = await forceRefreshSubscriptionFromDatabase();
                  setPaymentReturnStatus(confirmed ? 'success' : 'error');
                }}
              >
                <Text style={styles.paymentBannerButtonText}>{t('paymentRefreshButton')}</Text>
              </TouchableOpacity>
            </View>
          )}

          {authState.user && !isSubscribed && !showDCBFlow && paymentReturnStatus === 'idle' && (Platform.OS === 'web' ? (revenueCatEnabledWeb || dcbEnabledWeb) : revenueCatEnabledApp) && (
            <PremiumSubscriptionCard
              onSubscribe={async (method: PaymentMethod, productId?: string) => {
                if (method === 'dcb') {
                  const origin = typeof window !== 'undefined' ? window.location.origin : '';
                  const supabaseUrl = getSupabaseUrl();
                  const supabaseAnonKey = getSupabaseAnonKey();
                  if (!origin || !supabaseUrl || !supabaseAnonKey) return;
                  try {
                    const transactionId = `dcb_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
                    const response = await fetch(`${supabaseUrl}/functions/v1/dcb-init`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${supabaseAnonKey}`,
                        'Apikey': supabaseAnonKey,
                      },
                      body: JSON.stringify({
                        callback_ok: `${origin}/dcb-callback?dcb_ok=true`,
                        callback_ko: `${origin}/dcb-callback?dcb_cancel=1`,
                        transaction_id: transactionId,
                        smartuser_user_id: authState.user?.smartuserId,
                        campaign_countries_languages_id: campaignCountriesLanguagesId,
                      }),
                    });
                    if (!response.ok) return;
                    const data = await response.json();
                    if (data.url_redirect) {
                      const newTab = window.open(data.url_redirect, '_blank');
                      if (!newTab) window.location.href = data.url_redirect;
                    }
                  } catch (_) {}
                } else if (method === 'revenuecat_web') {
                  const baseUrl = REVENUECAT_CONFIG.web.purchaseLink;
                  const userId = authState.user?.smartuserId;
                  if (!baseUrl || !userId) {
                    Alert.alert(t('error'), t('paymentUnavailable'));
                    return;
                  }
                  setWebPaymentUrl(`${baseUrl}/${userId}`);
                  setShowWebPaymentModal(true);
                } else if (method === 'revenuecat_app') {
                  console.log('[Profile] Triggering native purchase', {
                    isIAPReady,
                    isPurchasing,
                    productsCount: products.length,
                    selectedProductId: productId,
                  });
                  try {
                    await purchaseSubscription(productId || products[0]?.productId);
                  } catch (error: any) {
                    if (!error?.userCancelled) {
                      console.error('Native purchase error:', error);
                      Alert.alert(t('error'), error?.message || t('purchaseFailed'));
                    }
                  }
                }
              }}
              campaignCountriesLanguagesId={campaignCountriesLanguagesId}
              products={products}
              isPurchasing={isPurchasing}
              isIAPReady={isIAPReady}
            />
          )}
        </View>

        {/* User Profile Information */}
        {authState.user && !showDCBFlow && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>{t('profileInformation')}</Text>
              <TouchableOpacity
                onPress={() => {
                  console.log('Edit button pressed');
                  openProfileEditor();
                }}
              >
                <Pencil size={20} color={primaryColor} />
              </TouchableOpacity>
            </View>
            
            <View style={styles.profileInfoContainer}>
              <View style={styles.profileInfoItem}>
                <User size={16} color="#666" />
                <TouchableOpacity
                  style={styles.profileInfoContent}
                  onPress={openProfileEditor}
                >
                  <Text style={styles.profileInfoLabel}>{t('pseudo')}</Text>
                  <Text style={styles.profileInfoValue}>
                    {userProfile.pseudo || authState.user?.pseudo || generateDefaultPseudo(authState.user?.smartuserId) || t('notProvided')}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Always show email field if user has one, or show it empty if they signed up with phone */}
              <View style={styles.profileInfoItem}>
                <Mail size={16} color="#666" />
                <TouchableOpacity
                  style={styles.profileInfoContent}
                  onPress={openProfileEditor}
                >
                  <Text style={styles.profileInfoLabel}>{t('email')}</Text>
                  <Text style={styles.profileInfoValue}>
                    {userProfile.email || t('notProvided')}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Always show phone field if user has one, or show it empty if they signed up with email */}
              <View style={styles.profileInfoItem}>
                <Phone size={16} color="#666" />
                <TouchableOpacity
                  style={styles.profileInfoContent}
                  onPress={openProfileEditor}
                >
                  <Text style={styles.profileInfoLabel}>{t('phoneNumber')}</Text>
                  <Text style={styles.profileInfoValue}>
                    {userProfile.phoneNumber || t('notProvided')}
                  </Text>
                </TouchableOpacity>
              </View>

              <View style={styles.profileInfoItem}>
                <Calendar size={16} color="#666" />
                <TouchableOpacity
                  style={styles.profileInfoContent}
                  onPress={openProfileEditor}
                >
                  <Text style={styles.profileInfoLabel}>{t('dateOfBirth')}</Text>
                  <Text style={styles.profileInfoValue}>
                    {userProfile.dateOfBirth ? formatDateForDisplay(userProfile.dateOfBirth, countryCode) : t('notProvided')}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}

        {/* Biometric Authentication Section - Native only */}
        {authState.user && !showDCBFlow && biometricSupport.isAvailable && biometricSupport.isEnrolled && (Platform.OS === 'ios' || Platform.OS === 'android') && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('security')}</Text>
            <View style={{ height: 12 }} />

            <View style={styles.biometricContainer}>
              <TouchableOpacity
                style={styles.biometricItem}
                onPress={handleToggleBiometric}
              >
                <View style={[styles.biometricIcon, { backgroundColor: `${primaryColor}33` }]}>
                  <Fingerprint size={20} color={primaryColor} />
                </View>
                <View style={styles.biometricContent}>
                  <Text style={styles.biometricTitle}>
                    {Platform.OS === 'android'
                      ? t('biometricLoginTitle')
                      : biometricSupport.supportedTypes[0] === 'Face ID'
                        ? t('faceIdLogin')
                        : biometricSupport.supportedTypes[0] === 'Touch ID'
                          ? t('touchIdLogin')
                          : t('biometricLoginTitle')
                    }
                  </Text>
                  <Text style={styles.biometricDescription}>
                    {Platform.OS === 'android'
                      ? (biometricEnabled
                          ? t('useBiometricToSignIn')
                          : t('enableBiometricForSignIn')
                        )
                      : biometricSupport.supportedTypes[0] === 'Face ID'
                        ? (biometricEnabled
                            ? t('useFaceIdToSignIn')
                            : t('enableFaceIdForSignIn')
                          )
                        : biometricSupport.supportedTypes[0] === 'Touch ID'
                          ? (biometricEnabled
                              ? t('useTouchIdToSignIn')
                              : t('enableTouchIdForSignIn')
                            )
                          : (biometricEnabled
                              ? t('useBiometricToSignIn')
                              : t('enableBiometricForSignIn')
                            )
                    }
                  </Text>
                </View>
                <View style={[
                  styles.biometricToggle,
                  biometricEnabled && styles.biometricToggleActive,
                  biometricEnabled && { backgroundColor: primaryColor }
                ]}>
                  <View style={[
                    styles.biometricToggleThumb,
                    biometricEnabled && styles.biometricToggleThumbActive
                  ]} />
                </View>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Debug Section */}
        {showDebugTools && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Debug Tools</Text>
            <View style={{ height: 12 }} />

            {/* Snowplow Tracking Toggle */}
            <TouchableOpacity
              style={[
                styles.debugTrackingToggle,
                isSnowplowDisabled && styles.debugTrackingToggleDisabled
              ]}
              onPress={toggleSnowplow}
              activeOpacity={0.7}
            >
              <View style={styles.debugTrackingToggleContent}>
                {isSnowplowDisabled ? (
                  <EyeOff size={20} color="#dc3545" />
                ) : (
                  <Eye size={20} color="#28a745" />
                )}
                <View style={styles.debugTrackingToggleText}>
                  <Text style={styles.debugTrackingToggleTitle}>
                    Snowplow Tracking
                  </Text>
                  <Text style={[
                    styles.debugTrackingToggleStatus,
                    isSnowplowDisabled ? styles.debugTrackingStatusOff : styles.debugTrackingStatusOn
                  ]}>
                    {isSnowplowDisabled ? 'DISABLED (session only)' : 'ENABLED'}
                  </Text>
                </View>
              </View>
              <View style={[
                styles.debugTrackingToggleIndicator,
                isSnowplowDisabled ? styles.indicatorOff : styles.indicatorOn
              ]} />
            </TouchableOpacity>

            <View style={{ height: 16 }} />

            <View style={styles.storageStatsContainer}>
              <Text style={styles.storageStatsTitle}>User Locale</Text>
              <View style={styles.storageStatRow}>
                <Text style={styles.storageStatLabel}>Country Code:</Text>
                <Text style={[styles.storageStatValue, { color: primaryColor }]}>{countryLoading ? '...' : (countryCode || 'N/A')}</Text>
              </View>
              <View style={styles.storageStatRow}>
                <Text style={styles.storageStatLabel}>Language Code:</Text>
                <Text style={[styles.storageStatValue, { color: primaryColor }]}>{language || 'N/A'}</Text>
              </View>
            </View>

            <View style={{ height: 16 }} />

            <View style={styles.storageStatsContainer}>
              <Text style={styles.storageStatsTitle}>Subscription Info</Text>
              <View style={styles.storageStatRow}>
                <Text style={styles.storageStatLabel}>isSubscribed (hook):</Text>
                <Text style={[styles.storageStatValue, { color: isSubscribed ? '#51cf66' : '#ff6b6b' }]}>
                  {String(isSubscribed)}
                </Text>
              </View>
              <View style={styles.storageStatRow}>
                <Text style={styles.storageStatLabel}>isSubscribed (auth):</Text>
                <Text style={[styles.storageStatValue, { color: authState.user?.isSubscribed ? '#51cf66' : '#ff6b6b' }]}>
                  {String(authState.user?.isSubscribed ?? 'N/A')}
                </Text>
              </View>
              <View style={styles.storageStatRow}>
                <Text style={styles.storageStatLabel}>Payment Method:</Text>
                <Text style={[styles.storageStatValue, { color: authState.user?.paymentMethod === 'dcb' ? '#ffd43b' : '#74c0fc' }]}>
                  {authState.user?.paymentMethod || 'N/A'}
                </Text>
              </View>
              <View style={styles.storageStatRow}>
                <Text style={styles.storageStatLabel}>SmartUser ID:</Text>
                <Text style={[styles.storageStatValue, { color: primaryColor }]}>{authState.user?.smartuserId || 'N/A'}</Text>
              </View>
            </View>

            <View style={{ height: 16 }} />

            <View style={styles.storageStatsContainer}>
              <Text style={styles.storageStatsTitle}>DCB Cookie Debug</Text>
              <TouchableOpacity
                style={[styles.menuItem, { marginBottom: 8 }]}
                onPress={testDcbCookie}
                disabled={dcbCookieLoading}
              >
                {dcbCookieLoading ? (
                  <ActivityIndicator size="small" color="#ffd43b" />
                ) : (
                  <Cookie size={20} color="#ffd43b" />
                )}
                <Text style={[styles.menuText, { color: '#ffd43b', flex: 1 }]}>
                  {dcbCookieLoading ? 'Testing...' : 'Test DCB Cookie'}
                </Text>
              </TouchableOpacity>
              {dcbCookieDebug && (
                <>
                  {dcbCookieDebug.error ? (
                    <Text style={[styles.storageStatLabel, { color: '#ff6b6b' }]}>Error: {dcbCookieDebug.error}</Text>
                  ) : (
                    <>
                      <View style={styles.storageStatRow}>
                        <Text style={styles.storageStatLabel}>Client Hostname:</Text>
                        <Text style={[styles.storageStatValue, { fontSize: 11, color: primaryColor }]}>{dcbCookieDebug.clientHostname}</Text>
                      </View>
                      <View style={styles.storageStatRow}>
                        <Text style={styles.storageStatLabel}>Supabase URL:</Text>
                        <Text style={[styles.storageStatValue, { fontSize: 10, color: primaryColor }]}>{dcbCookieDebug.supabaseUrl}</Text>
                      </View>
                      <View style={styles.storageStatRow}>
                        <Text style={styles.storageStatLabel}>Server Origin:</Text>
                        <Text style={[styles.storageStatValue, { fontSize: 10, color: primaryColor }]}>{dcbCookieDebug.detectedOrigin || 'N/A'}</Text>
                      </View>
                      <View style={styles.storageStatRow}>
                        <Text style={styles.storageStatLabel}>Cookie Domain:</Text>
                        <Text style={[styles.storageStatValue, { color: primaryColor }]}>{dcbCookieDebug.resolvedCookieDomain || 'N/A'}</Text>
                      </View>
                      <View style={styles.storageStatRow}>
                        <Text style={styles.storageStatLabel}>SameSite:</Text>
                        <Text style={[styles.storageStatValue, { color: primaryColor }]}>{dcbCookieDebug.sameSiteMode || 'N/A'}</Text>
                      </View>
                      <View style={styles.storageStatRow}>
                        <Text style={styles.storageStatLabel}>Cookie Present:</Text>
                        <Text style={[styles.storageStatValue, { color: dcbCookieDebug.cookiePresent ? '#51cf66' : '#ff6b6b' }]}>
                          {dcbCookieDebug.cookiePresent ? 'YES' : 'NO'}
                        </Text>
                      </View>
                      {dcbCookieDebug.cookieIdPreview && (
                        <View style={styles.storageStatRow}>
                          <Text style={styles.storageStatLabel}>Cookie ID:</Text>
                          <Text style={[styles.storageStatValue, { color: primaryColor }]}>{dcbCookieDebug.cookieIdPreview}</Text>
                        </View>
                      )}
                      {dcbCookieDebug.pendingSubscription ? (
                        <>
                          <View style={[styles.storageStatRow, { marginTop: 4 }]}>
                            <Text style={styles.storageStatLabel}>Pending Status:</Text>
                            <Text style={[styles.storageStatValue, {
                              color: dcbCookieDebug.pendingSubscription.status === 'pending' ? '#51cf66' :
                                     dcbCookieDebug.pendingSubscription.status === 'expired' ? '#ff6b6b' : '#74c0fc'
                            }]}>
                              {dcbCookieDebug.pendingSubscription.status}
                            </Text>
                          </View>
                          <View style={styles.storageStatRow}>
                            <Text style={styles.storageStatLabel}>MSISDN:</Text>
                            <Text style={[styles.storageStatValue, { color: primaryColor }]}>{dcbCookieDebug.pendingSubscription.msisdn || 'N/A'}</Text>
                          </View>
                          <View style={styles.storageStatRow}>
                            <Text style={styles.storageStatLabel}>Operation ID:</Text>
                            <Text style={[styles.storageStatValue, { fontSize: 10, color: primaryColor }]}>{dcbCookieDebug.pendingSubscription.operationId || 'N/A'}</Text>
                          </View>
                          <View style={styles.storageStatRow}>
                            <Text style={styles.storageStatLabel}>Created:</Text>
                            <Text style={[styles.storageStatValue, { fontSize: 11, color: primaryColor }]}>{dcbCookieDebug.pendingSubscription.createdAt || 'N/A'}</Text>
                          </View>
                          <View style={styles.storageStatRow}>
                            <Text style={styles.storageStatLabel}>Expires:</Text>
                            <Text style={[styles.storageStatValue, { fontSize: 11, color: primaryColor }]}>{dcbCookieDebug.pendingSubscription.expiresAt || 'N/A'}</Text>
                          </View>
                          <View style={styles.storageStatRow}>
                            <Text style={styles.storageStatLabel}>Temp Ref:</Text>
                            <Text style={[styles.storageStatValue, { fontSize: 10, color: primaryColor }]}>{dcbCookieDebug.pendingSubscription.temporaryAbsoluteReference || 'N/A'}</Text>
                          </View>
                        </>
                      ) : dcbCookieDebug.cookiePresent ? (
                        <Text style={[styles.storageStatLabel, { color: '#ffd43b', marginTop: 4 }]}>
                          Cookie found but no pending subscription in DB
                        </Text>
                      ) : null}
                      {Platform.OS === 'web' && (
                        <>
                          <View style={[styles.storageStatRow, { marginTop: 8, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.1)', paddingTop: 8 }]}>
                            <Text style={styles.storageStatLabel}>localStorage operationId:</Text>
                            <Text style={[styles.storageStatValue, { fontSize: 10, color: (() => { try { return localStorage.getItem('dcb_last_operation_id') ? '#51cf66' : '#ff6b6b'; } catch { return '#ff6b6b'; } })() }]}>
                              {(() => { try { return localStorage.getItem('dcb_last_operation_id') || 'NOT SET'; } catch { return 'NOT SET'; } })()}
                            </Text>
                          </View>
                          <View style={styles.storageStatRow}>
                            <Text style={styles.storageStatLabel}>localStorage storedAt:</Text>
                            <Text style={[styles.storageStatValue, { fontSize: 10, color: primaryColor }]}>
                              {(() => { try { const ts = localStorage.getItem('dcb_operation_id_stored_at'); return ts ? new Date(Number(ts)).toISOString() : 'N/A'; } catch { return 'N/A'; } })()}
                            </Text>
                          </View>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </View>

            <View style={{ height: 16 }} />

            {/* Loading indicator or Stats */}
            {!asyncStorageStats ? (
              <View style={styles.storageStatsContainer}>
                <Text style={styles.storageStatLabel}>Press "Refresh Debug Stats" to load data</Text>
              </View>
            ) : asyncStorageStats && asyncStorageStats.prefetchMode ? (
              <View style={styles.storageStatsContainer}>
                <Text style={styles.storageStatsTitle}>Content Cache</Text>
                <View style={styles.storageStatRow}>
                  <Text style={styles.storageStatLabel}>Rubriques:</Text>
                  <Text style={[styles.storageStatValue, { color: primaryColor }]}>{asyncStorageStats.rubriquesCount}</Text>
                </View>
                <View style={styles.storageStatRow}>
                  <Text style={styles.storageStatLabel}>Series:</Text>
                  <Text style={[styles.storageStatValue, { color: primaryColor }]}>{asyncStorageStats.seriesCount}</Text>
                </View>
                <View style={styles.storageStatRow}>
                  <Text style={styles.storageStatLabel}>Free Episodes per Series:</Text>
                  <Text style={[styles.storageStatValue, { color: '#51cf66' }]}>{asyncStorageStats.freeEpisodesCount}</Text>
                </View>

                <Text style={[styles.storageStatsTitle, { marginTop: 16 }]}>Episodes Cache</Text>
                <View style={styles.storageStatRow}>
                  <Text style={styles.storageStatLabel}>Cached Series with Episodes:</Text>
                  <Text style={[styles.storageStatValue, { color: primaryColor }]}>{asyncStorageStats.cachedSeriesCount}</Text>
                </View>
                <View style={styles.storageStatRow}>
                  <Text style={styles.storageStatLabel}>Total Cached Episodes:</Text>
                  <Text style={[styles.storageStatValue, styles.highlightValue]}>{asyncStorageStats.totalCachedEpisodes}</Text>
                </View>
                <View style={styles.storageStatRow}>
                  <Text style={styles.storageStatLabel}>Avg Episodes per Series:</Text>
                  <Text style={[styles.storageStatValue, { color: primaryColor }]}>{asyncStorageStats.avgEpisodesPerSeries}</Text>
                </View>

                {/* Cache Performance */}
                {asyncStorageStats.cacheStatsData && (
                  <>
                    <Text style={[styles.storageStatsTitle, { marginTop: 16 }]}>Cache Performance</Text>
                    <View style={styles.storageStatRow}>
                      <Text style={styles.storageStatLabel}>Hit Rate:</Text>
                      <Text style={[
                        styles.storageStatValue,
                        { color: asyncStorageStats.cacheStatsData.hitRate >= 80 ? '#51cf66' : '#ffd43b' }
                      ]}>
                        {asyncStorageStats.cacheStatsData.hitRate}%
                      </Text>
                    </View>
                    <View style={styles.storageStatRow}>
                      <Text style={styles.storageStatLabel}>Cache Hits:</Text>
                      <Text style={[styles.storageStatValue, { color: primaryColor }]}>{asyncStorageStats.cacheStatsData.cacheHits}</Text>
                    </View>
                    <View style={styles.storageStatRow}>
                      <Text style={styles.storageStatLabel}>Cache Misses:</Text>
                      <Text style={[styles.storageStatValue, { color: primaryColor }]}>{asyncStorageStats.cacheStatsData.cacheMisses}</Text>
                    </View>
                    <View style={styles.storageStatRow}>
                      <Text style={styles.storageStatLabel}>API Calls:</Text>
                      <Text style={[styles.storageStatValue, { color: primaryColor }]}>{asyncStorageStats.cacheStatsData.apiCalls}</Text>
                    </View>
                  </>
                )}

                {/* Cached Series List */}
                {asyncStorageStats.cachedSeriesList && asyncStorageStats.cachedSeriesList.length > 0 && (
                  <>
                    <Text style={[styles.storageStatsTitle, { marginTop: 16 }]}>
                      Cached Series ({asyncStorageStats.cachedSeriesList.length})
                    </Text>
                    <ScrollView
                      style={{ maxHeight: 200 }}
                      nestedScrollEnabled={true}
                    >
                      {asyncStorageStats.cachedSeriesList.slice(0, 20).map((series, index) => (
                        <View key={series.seriesId} style={styles.seriesListItem}>
                          <Text style={styles.seriesIdText}>#{series.seriesId}</Text>
                          <Text style={styles.seriesEpisodeCount}>{series.episodeCount} ep</Text>
                          <Text style={styles.seriesAgeText}>{series.cacheAge}</Text>
                        </View>
                      ))}
                      {asyncStorageStats.cachedSeriesList.length > 20 && (
                        <Text style={styles.moreSeriesText}>
                          ... and {asyncStorageStats.cachedSeriesList.length - 20} more
                        </Text>
                      )}
                    </ScrollView>
                  </>
                )}

                {/* Episode URLs for DRM Debugging */}
                <>
                  <Text style={[styles.storageStatsTitle, { marginTop: 16 }]}>
                    Episode URLs - Serie 9619162
                  </Text>
                  {asyncStorageStats.episodeUrls && asyncStorageStats.episodeUrls.length > 0 ? (
                    <ScrollView
                      style={{ maxHeight: 400 }}
                      nestedScrollEnabled={true}
                    >
                      {asyncStorageStats.episodeUrls.map((episode, index) => (
                        <View key={`${episode.seriesId}-${episode.episodeId}`} style={styles.episodeUrlItem}>
                          <Text style={styles.episodeIdText}>
                            S{episode.seriesId} - E{episode.episodeId}
                          </Text>
                          <Text style={styles.episodeTitleText} numberOfLines={1}>
                            {episode.episodeTitle}
                          </Text>
                          <View style={styles.urlStatusRow}>
                            <Text style={[
                              styles.urlStatusLabel,
                              { color: episode.hasVideoUrl ? '#51cf66' : '#ff4444' }
                            ]}>
                              Video: {episode.hasVideoUrl ? '✓' : '✗'}
                            </Text>
                            <Text style={[
                              styles.urlStatusLabel,
                              { color: episode.hasDrmUrl ? '#51cf66' : '#666' }
                            ]}>
                              DRM: {episode.hasDrmUrl ? '✓' : '✗'}
                            </Text>
                          </View>
                          {episode.videoUrl && (
                            <Text style={styles.urlText} numberOfLines={1}>
                              Video: {episode.videoUrl === 'EMPTY' ? 'EMPTY' : episode.videoUrl.substring(0, 50) + '...'}
                            </Text>
                          )}
                          {episode.drmUrl && (
                            <Text style={styles.urlText} numberOfLines={1}>
                              DRM: {episode.drmUrl === 'EMPTY' ? 'EMPTY' : episode.drmUrl.substring(0, 50) + '...'}
                            </Text>
                          )}
                        </View>
                      ))}
                    </ScrollView>
                  ) : (
                    <Text style={[styles.storageStatLabel, { marginTop: 8, color: '#ff4444' }]}>
                      Serie 9619162 not found in cache. Try clearing and refreshing the cache.
                    </Text>
                  )}
                </>
              </View>
            ) : (
              <View style={styles.storageStatsContainer}>
                <Text style={styles.storageStatsTitle}>No Stats Available</Text>
                <Text style={styles.storageStatLabel}>Press "Refresh Debug Stats" to load data</Text>
              </View>
            )}

            {/* Gamification Debug Section */}
            <View style={[styles.storageStatsContainer, { marginTop: 16 }]}>
              <Text style={styles.storageStatsTitle}>Gamification Cache</Text>

              {authState.user?.smartuserId ? (
                <>
                  <View style={styles.storageStatRow}>
                    <Text style={styles.storageStatLabel}>Coins (cache):</Text>
                    <Text style={[styles.storageStatValue, { color: '#ffd43b' }]}>
                      {userGamification?.total_coins ?? 'N/A'}
                    </Text>
                  </View>
                  <View style={styles.storageStatRow}>
                    <Text style={styles.storageStatLabel}>Streak actuel:</Text>
                    <Text style={[styles.storageStatValue, { color: primaryColor }]}>
                      {userGamification?.consecutive_days_streak ?? 0} jours
                    </Text>
                  </View>
                  <View style={styles.storageStatRow}>
                    <Text style={styles.storageStatLabel}>Derniere visite:</Text>
                    <Text style={[styles.storageStatValue, { color: primaryColor }]}>
                      {userGamification?.last_visit_date
                        ? new Date(userGamification.last_visit_date).toLocaleDateString('fr-FR')
                        : 'N/A'}
                    </Text>
                  </View>

                  {recentAchievements.length > 0 && (
                    <>
                      <Text style={[styles.storageStatsTitle, { marginTop: 16 }]}>
                        Historique recent ({recentAchievements.length})
                      </Text>
                      <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                        {recentAchievements.map((achievement, index) => {
                          const isDeduction = achievement.achievement_type === 'coins_used_for_premium';
                          const coinValue = isDeduction
                            ? -(achievement.metadata?.firstUseCost || achievement.metadata?.cost || 0)
                            : achievement.coins_earned;
                          const formattedDate = new Date(achievement.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          });

                          return (
                            <View key={achievement.id} style={[styles.storageStatRow, { paddingVertical: 4 }]}>
                              <Text style={[styles.storageStatLabel, { flex: 2, fontSize: 11 }]} numberOfLines={1}>
                                {achievement.achievement_type}
                              </Text>
                              <Text style={[
                                styles.storageStatValue,
                                {
                                  color: coinValue >= 0 ? '#51cf66' : '#ff6b6b',
                                  minWidth: 50,
                                  textAlign: 'right'
                                }
                              ]}>
                                {coinValue >= 0 ? '+' : ''}{coinValue}
                              </Text>
                              <Text style={[styles.storageStatLabel, { fontSize: 10, marginLeft: 8, color: '#888' }]}>
                                {formattedDate}
                              </Text>
                            </View>
                          );
                        })}
                      </ScrollView>
                    </>
                  )}

                  {recentAchievements.length === 0 && (
                    <Text style={[styles.storageStatLabel, { marginTop: 8, fontStyle: 'italic' }]}>
                      Aucun historique charge. Cliquez sur "Refresh Debug Stats"
                    </Text>
                  )}
                </>
              ) : (
                <Text style={styles.storageStatLabel}>Connectez-vous pour voir les stats gamification</Text>
              )}
            </View>

            <View>
            <TouchableOpacity
              style={styles.menuItem}
              onPress={loadAsyncStorageStats}
            >
              <HardDrive size={20} color="#4CAF50" />
              <Text style={[styles.menuText, { color: '#4CAF50', flex: 1 }]}>
                Refresh Debug Stats
              </Text>
              <ChevronRight size={16} color="#666" />
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.menuItem}
              onPress={handleTriggerFullPrefetch}
            >
              <HardDrive size={20} color="#4CAF50" />
              <Text style={[styles.menuText, { color: '#4CAF50', flex: 1 }]}>
                Force Full Prefetch
              </Text>
              <ChevronRight size={16} color="#666" />
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.menuItem}
              onPress={handleRefreshCampaignConfig}
            >
              <RefreshCw size={20} color="#2196F3" />
              <Text style={[styles.menuText, { color: '#2196F3', flex: 1 }]}>
                Refresh Campaign Config
              </Text>
              <ChevronRight size={16} color="#666" />
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.menuItem, styles.dangerButton]}
              onPress={handleClearEpisodesCache}
            >
              <Trash size={20} color="#ffd43b" />
              <Text style={[styles.menuText, { color: '#ffd43b', flex: 1 }]}>
                Clear Episodes Cache Only
              </Text>
              <ChevronRight size={16} color="#666" />
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.menuItem, styles.dangerButton]}
              onPress={handleClearAsyncStorage}
            >
              <Trash size={20} color="#ff4444" />
              <Text style={[styles.menuText, { color: '#ff4444', flex: 1 }]}>
                Clear ALL Caches
              </Text>
              <ChevronRight size={16} color="#666" />
            </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Menu Items */}
        <View style={styles.menuSection}>
          {dcbSubscriptionInfo && subscriptionState !== 'GRACE' && (
            <View style={styles.dcbSubscriptionCard}>
              <Text style={styles.dcbSubscriptionTitle}>{t(dcbOfferLabelKey)}</Text>
              <Text style={styles.dcbSubscriptionText}>
                {t('dcbSubscriptionValidity', { date: formatDcbDate(dcbSubscriptionInfo.nextPaymentDate) })}
              </Text>
            </View>
          )}

          {menuItems.map((item, index) => (
            <TouchableOpacity
              key={index}
              style={styles.menuItem}
              onPress={item.onPress}
            >
              <item.icon size={20} color="#ffffff" />
              <Text style={styles.menuText}>{item.title}</Text>
              {isRTL ? <ChevronLeft size={16} color="#666" /> : <ChevronRight size={16} color="#666" />}
            </TouchableOpacity>
          ))}

          {/* Cookies Expandable Section */}
          <TouchableOpacity
            style={styles.expandableHeader}
            onPress={() => setExpandedCookiesSection(!expandedCookiesSection)}
          >
            <Cookie size={20} color="#ffffff" />
            <Text style={styles.menuText}>{t('cookiesSection')}</Text>
            {expandedCookiesSection ? (
              <ChevronUp size={16} color="#666" />
            ) : (
              <ChevronDown size={16} color="#666" />
            )}
          </TouchableOpacity>
          {expandedCookiesSection && (
            <View style={styles.expandableContent}>
              {cookiesMenuItems.map((item, index) => (
                <TouchableOpacity
                  key={`cookies-${index}`}
                  style={styles.subMenuItem}
                  onPress={item.onPress}
                >
                  <item.icon size={18} color="#999" />
                  <Text style={styles.subMenuText}>{item.title}</Text>
                  {isRTL ? <ChevronLeft size={14} color="#666" /> : <ChevronRight size={14} color="#666" />}
                </TouchableOpacity>
              ))}
            </View>
          )}

          {/* Legal Expandable Section */}
          <TouchableOpacity
            style={styles.expandableHeader}
            onPress={() => setExpandedLegalSection(!expandedLegalSection)}
          >
            <Scale size={20} color="#ffffff" />
            <Text style={styles.menuText}>{t('legalSection')}</Text>
            {expandedLegalSection ? (
              <ChevronUp size={16} color="#666" />
            ) : (
              <ChevronDown size={16} color="#666" />
            )}
          </TouchableOpacity>
          {expandedLegalSection && (
            <View style={styles.expandableContent}>
              {legalMenuItems.map((item, index) => (
                <TouchableOpacity
                  key={`legal-${index}`}
                  style={styles.subMenuItem}
                  onPress={item.onPress}
                >
                  <item.icon size={18} color="#999" />
                  <Text style={styles.subMenuText}>{item.title}</Text>
                  {isRTL ? <ChevronLeft size={14} color="#666" /> : <ChevronRight size={14} color="#666" />}
                </TouchableOpacity>
              ))}
            </View>
          )}

          {subscriptionState === 'GRACE' && gracePeriodEndDate && (
            <View style={styles.gracePeriodBanner}>
              <Text style={styles.gracePeriodTitle}>{t('gracePeriodBannerTitle')}</Text>
              <Text style={styles.gracePeriodText}>
                {t('gracePeriodBannerMessage', { date: new Date(gracePeriodEndDate).toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : language === 'ar' ? 'ar' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' }) })}
              </Text>
            </View>
          )}

          {subscriptionState === 'NOTBILLED_DENY' && (
            <View style={styles.notBilledDenyBanner}>
              <Text style={styles.notBilledDenyTitle}>{t('notBilledDenyBannerTitle')}</Text>
              <Text style={styles.notBilledDenyText}>{t('notBilledDenyBannerMessage')}</Text>
            </View>
          )}

          {isSubscribed && subscriptionState !== 'GRACE' && (
            <TouchableOpacity style={styles.menuItem} onPress={handleUnsubscribePress}>
              <UserX size={20} color="#ffffff" />
              <Text style={styles.menuText}>{t('unsubscribe')}</Text>
              {isRTL ? <ChevronLeft size={16} color="#666" /> : <ChevronRight size={16} color="#666" />}
            </TouchableOpacity>
          )}

          {authState.user && !showDCBFlow && (
            <>
              <TouchableOpacity style={styles.menuItem} onPress={() => setShowDeleteAccountModal(true)}>
                <User size={20} color="#ff4444" />
                <Text style={[styles.menuText, { color: '#ff4444' }]}>{t('deleteMyAccount')}</Text>
                {isRTL ? <ChevronLeft size={16} color="#666" /> : <ChevronRight size={16} color="#666" />}
              </TouchableOpacity>

              <TouchableOpacity style={styles.menuItem} onPress={handleLogout}>
                <User size={20} color="#ff4444" />
                <Text style={[styles.menuText, { color: '#ff4444' }]}>{t('signOut')}</Text>
                {isRTL ? <ChevronLeft size={16} color="#666" /> : <ChevronRight size={16} color="#666" />}
              </TouchableOpacity>
            </>
          )}
        </View>
        {/* App Version */}
        <TouchableOpacity
          style={styles.versionContainer}
          onPress={() => {
            const newCount = versionTapCount + 1;
            setVersionTapCount(newCount);

            if (newCount === 7) {
              if (showDebugTools) {
                setShowDebugTools(false);
                setDebugModeActive(false);
                Alert.alert(
                  'Debug Mode',
                  'Debug tools have been disabled!',
                  [{ text: 'OK' }]
                );
              } else {
                setShowDebugTools(true);
                setDebugModeActive(true);
                Alert.alert(
                  'Debug Mode',
                  'Debug tools have been enabled!',
                  [{ text: 'OK' }]
                );
              }
              setVersionTapCount(0);
              if (versionTapTimeoutRef.current) {
                clearTimeout(versionTapTimeoutRef.current);
                versionTapTimeoutRef.current = null;
              }
            } else {
              // Reset counter after 2 seconds of inactivity
              if (versionTapTimeoutRef.current) {
                clearTimeout(versionTapTimeoutRef.current);
              }
              versionTapTimeoutRef.current = setTimeout(() => {
                setVersionTapCount(0);
                versionTapTimeoutRef.current = null;
              }, 2000);
            }
          }}
          activeOpacity={0.7}
        >
          <Text style={styles.versionText}>
            {t('version')} {Constants.expoConfig?.version || '1.0.0'}
          </Text>
        </TouchableOpacity>
      </ScrollView>
      </View>
      </KeyboardAvoidingView>
    </SafeAreaView>

    <LanguageSelector
      visible={showLanguageSelector}
      onClose={() => setShowLanguageSelector(false)}
      availableLanguages={availableLanguages}
    />

    {Platform.OS !== 'web' && revenueCatEnabledApp && (
      <CustomPaywall
        visible={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        source="profile"
      />
    )}

    {Platform.OS === 'web' && (revenueCatEnabledWeb || dcbEnabledWeb) && (
      <WebPaymentModal
        visible={showWebPaymentModal}
        onClose={() => {
          setShowWebPaymentModal(false);
          if (authState.user?.smartuserId) {
            setPaymentReturnStatus('verifying');
            forceRefreshSubscriptionFromDatabase().then((confirmed) => {
              if (confirmed) {
                updateUserSubscription(true);
                updateUserPaymentMethod('stripe');
                setPaymentReturnStatus('success');
              } else {
                checkSubscriptionStatus().then(() => {
                  setPaymentReturnStatus('idle');
                });
              }
            }).catch(() => {
              setPaymentReturnStatus('idle');
            });
          }
        }}
        paymentUrl={webPaymentUrl}
        onPaymentDetected={() => {
          setShowWebPaymentModal(false);
          setPaymentReturnStatus('verifying');
          updateUserSubscription(true);
          updateUserPaymentMethod('stripe');
          forceRefreshSubscriptionFromDatabase().then((confirmed) => {
            setPaymentReturnStatus(confirmed ? 'success' : 'success');
          }).catch(() => {
            setPaymentReturnStatus('success');
          });
        }}
        smartuserUserId={authState.user?.smartuserId}
        campaignCountriesLanguagesId={campaignCountriesLanguagesId}
      />
    )}


    {/* Profile Editor Modal */}
    <Modal
      visible={showProfileEditor}
      transparent
      animationType="slide"
      onRequestClose={() => setShowProfileEditor(false)}
    >
      <KeyboardAvoidingView
        style={[styles.modalOverlay, isDesktopWeb && styles.modalOverlayDesktopCenter]}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={[styles.modalContainer, isDesktopWeb && styles.modalContainerDesktop]}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('editProfile')}</Text>
            <TouchableOpacity onPress={() => setShowProfileEditor(false)}>
              <X size={24} color="#ffffff" />
            </TouchableOpacity>
          </View>

          <ScrollView
            ref={profileScrollViewRef}
            style={styles.modalContent}
            contentContainerStyle={styles.modalContentContainer}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
            showsVerticalScrollIndicator={false}
          >
            {/* Pseudo input */}
            <View
              style={styles.inputContainer}
              onLayout={(e) => handleInputLayout('pseudo', e)}
            >
              <Text style={styles.inputLabel}>{t('pseudo')}</Text>
              <TextInput
                style={styles.input}
                value={editingProfile.pseudo}
                onChangeText={(text) => {
                  setPseudoError('');
                  if (text.length <= 16) {
                    setEditingProfile(prev => ({ ...prev, pseudo: text }));
                  }
                }}
                onFocus={() => scrollToInput('pseudo')}
                placeholder={t('pseudoPlaceholder')}
                placeholderTextColor="#666"
                maxLength={16}
                autoCapitalize="none"
                returnKeyType="done"
                blurOnSubmit={true}
                onSubmitEditing={() => {
                  Keyboard.dismiss();
                }}
              />
              <Text style={styles.inputHint}>
                {t('pseudoCharactersCount', { count: editingProfile.pseudo.length })}
              </Text>
              {pseudoError ? (
                <Text style={styles.errorText}>{pseudoError}</Text>
              ) : null}
            </View>

            {/* Email input - always show */}
            <View
              style={styles.inputContainer}
              onLayout={(e) => handleInputLayout('email', e)}
            >
              <Text style={styles.inputLabel}>{t('email')}</Text>
              <TextInput
                style={styles.input}
                value={editingProfile.email}
                onChangeText={(text) => {
                  setEmailError('');
                  setEditingProfile(prev => ({ ...prev, email: text }));
                }}
                onFocus={() => scrollToInput('email')}
                placeholder={t('enterEmail')}
                placeholderTextColor="#666"
                keyboardType="email-address"
                autoCapitalize="none"
                returnKeyType="done"
                blurOnSubmit={true}
                onSubmitEditing={() => {
                  Keyboard.dismiss();
                }}
              />
              {emailError ? (
                <Text style={styles.errorText}>{emailError}</Text>
              ) : null}
            </View>

            {/* Phone number input - always show */}
            <View
              style={styles.inputContainer}
              onLayout={(e) => handleInputLayout('phone', e)}
            >
              <Text style={styles.inputLabel}>{t('phoneNumber')}</Text>
              <TextInput
                style={styles.input}
                value={editingProfile.phoneNumber}
                onChangeText={(text) => {
                  setPhoneError('');
                  setEditingProfile(prev => ({ ...prev, phoneNumber: text }));
                }}
                onFocus={() => scrollToInput('phone')}
                placeholder={t('enterPhoneNumber')}
                placeholderTextColor="#666"
                keyboardType="phone-pad"
                autoCapitalize="none"
                returnKeyType="done"
                blurOnSubmit={true}
                onSubmitEditing={() => {
                  Keyboard.dismiss();
                }}
              />
              {phoneError ? (
                <Text style={styles.errorText}>{phoneError}</Text>
              ) : null}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>{t('dateOfBirth')}</Text>
              {Platform.OS === 'web' ? (
                <View style={[styles.input, { flexDirection: 'row', alignItems: 'center', position: 'relative' as any }]}>
                  <input
                    type="date"
                    value={editingProfile.dateOfBirth || ''}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      setEditingProfile(prev => ({ ...prev, dateOfBirth: value || '' }));
                    }}
                    max={new Date().toISOString().split('T')[0]}
                    min="1900-01-01"
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#ffffff',
                      fontSize: 16,
                      fontFamily: 'inherit',
                      width: '100%',
                      outline: 'none',
                      cursor: 'pointer',
                      colorScheme: 'dark',
                    } as any}
                  />
                  <View style={{ pointerEvents: 'none' as any }}>
                    <Calendar size={20} color={primaryColor} />
                  </View>
                </View>
              ) : (
                <TouchableOpacity style={styles.input} onPress={openDatePicker}>
                  <Text style={[styles.dateText, !editingProfile.dateOfBirth && styles.placeholderText]}>
                    {editingProfile.dateOfBirth ? formatDateForDisplay(editingProfile.dateOfBirth, countryCode) : getLocalizedDatePattern(countryCode)}
                  </Text>
                </TouchableOpacity>
              )}
              <Text style={styles.inputHint}>
                {t('dateFormatHint').replace('{format}', getLocalizedDatePattern(countryCode)).replace('{example}', getDateExample(countryCode))}
              </Text>
            </View>
          </ScrollView>

          <View style={[styles.modalActions, { paddingBottom: Math.max(20, insets.bottom + 10) }]}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => {
                setEditingProfile(userProfile);
                setShowProfileEditor(false);
              }}
            >
              <Text style={styles.cancelButtonText}>{t('cancel')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.saveButton, { backgroundColor: primaryColor }, isUpdating && styles.buttonDisabled]}
              onPress={updateUserProfile}
              disabled={isUpdating}
            >
              <Check size={16} color="#ffffff" />
              <Text style={styles.saveButtonText}>
                {isUpdating ? t('saving') : t('save')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>

    {/* Native Date Picker for React Native */}
    {Platform.OS === 'ios' && showDatePicker && (
      <View style={styles.datePickerContainer}>
        <View style={styles.datePickerModal}>
          <View style={styles.datePickerHeader}>
            <TouchableOpacity onPress={cancelDateSelection} style={styles.datePickerButton}>
              <Text style={styles.datePickerButtonText}>{t('cancel')}</Text>
            </TouchableOpacity>
            <Text style={styles.datePickerTitle}>{t('dateOfBirth')}</Text>
            <TouchableOpacity onPress={confirmDateSelection} style={styles.datePickerButton}>
              <Text style={[styles.datePickerButtonText, styles.datePickerOkButton, { color: primaryColor }]}>OK</Text>
            </TouchableOpacity>
          </View>
          <RNDateTimePicker
            value={getDateValue()}
            mode="date"
            display="spinner"
            onChange={handleDateChange}
            maximumDate={new Date()}
            minimumDate={new Date(1900, 0, 1)}
            locale={getLocaleForDatePicker()}
            textColor="#ffffff"
            style={styles.datePicker}
          />
        </View>
      </View>
    )}

    {/* Android Date Picker - uses spinner/rolodex style for easier year selection */}
    {Platform.OS === 'android' && showDatePicker && (
      <RNDateTimePicker
        value={getDateValue()}
        mode="date"
        display="spinner"
        onChange={handleDateChange}
        maximumDate={new Date()}
        minimumDate={new Date(1900, 0, 1)}
        locale={getLocaleForDatePicker()}
      />
    )}

    {/* Delete Account Confirmation Modal */}
    <Modal
      visible={showDeleteAccountModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowDeleteAccountModal(false)}
    >
      <View style={styles.deleteModalOverlay}>
        <View style={styles.deleteModalContainer}>
          <View style={styles.deleteModalHeader}>
            <Text style={styles.deleteModalTitle}>{t('deleteAccountConfirmation')}</Text>
          </View>

          <ScrollView style={styles.deleteModalContent}>
            <Text style={styles.deleteModalWarning}>
              {isSubscribed ? t('deleteAccountWarningSubscribed') : t('deleteAccountWarningNotSubscribed')}
            </Text>
          </ScrollView>

          <View style={styles.deleteModalActions}>
            <TouchableOpacity
              style={styles.deleteCancelButton}
              onPress={() => setShowDeleteAccountModal(false)}
              disabled={isDeletingAccount}
            >
              <Text style={styles.deleteCancelButtonText}>{t('cancel')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.deleteConfirmButton, { backgroundColor: primaryColor }, isDeletingAccount && styles.buttonDisabled]}
              onPress={handleDeleteAccount}
              disabled={isDeletingAccount}
            >
              <Text style={styles.deleteConfirmButtonText}>
                {isDeletingAccount ? t('deletingAccount') : t('iConfirm')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>

    {/* Active Subscription Error Modal */}
    <Modal
      visible={showActiveSubErrorModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowActiveSubErrorModal(false)}
    >
      <View style={styles.deleteModalOverlay}>
        <View style={styles.deleteModalContainer}>
          <View style={styles.deleteModalHeader}>
            <Text style={styles.deleteModalTitle}>{t('activeSubscriptionErrorTitle')}</Text>
          </View>

          <ScrollView style={styles.deleteModalContent}>
            <Text style={styles.deleteModalWarning}>
              {t('activeSubscriptionErrorMessage')}
            </Text>
          </ScrollView>

          <View style={styles.deleteModalActions}>
            <TouchableOpacity
              style={styles.deleteCancelButton}
              onPress={() => setShowActiveSubErrorModal(false)}
            >
              <Text style={styles.deleteCancelButtonText}>{t('cancel')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.deleteConfirmButton, { backgroundColor: primaryColor }]}
              onPress={() => {
                setShowActiveSubErrorModal(false);
                handleUnsubscribePress();
              }}
            >
              <Text style={[styles.deleteConfirmButtonText, { color: '#ffffff' }]}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>

    {/* Grace Period Modal */}
    <Modal
      visible={showGracePeriodModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowGracePeriodModal(false)}
    >
      <View style={styles.deleteModalOverlay}>
        <View style={styles.deleteModalContainer}>
          <View style={styles.deleteModalHeader}>
            <Text style={styles.deleteModalTitle}>{t('gracePeriodErrorTitle')}</Text>
          </View>

          <ScrollView style={styles.deleteModalContent}>
            <Text style={styles.deleteModalWarning}>
              {t('gracePeriodErrorMessage')}
            </Text>
          </ScrollView>

          <View style={styles.deleteModalActions}>
            <TouchableOpacity
              style={[styles.deleteConfirmButton, { backgroundColor: primaryColor }]}
              onPress={() => setShowGracePeriodModal(false)}
            >
              <Text style={[styles.deleteConfirmButtonText, { color: '#ffffff' }]}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>

    {/* DCB Unsubscribe Confirm Modal */}
    <Modal
      visible={showDcbUnsubModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowDcbUnsubModal(false)}
    >
      <View style={styles.dcbUnsubOverlay}>
        <View style={styles.dcbUnsubContent}>
          <Text style={styles.dcbUnsubTitle}>{t('dcbUnsubscribeConfirmTitle')}</Text>
          <Text style={styles.dcbUnsubMessage}>{t('dcbUnsubscribeConfirmMessage')}</Text>
          <View style={styles.dcbUnsubButtons}>
            <TouchableOpacity
              style={styles.dcbUnsubCancelButton}
              onPress={() => setShowDcbUnsubModal(false)}
            >
              <Text style={styles.dcbUnsubCancelText}>{t('cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.dcbUnsubConfirmButton, { backgroundColor: primaryColor }]}
              onPress={handleDcbUnsubscribe}
            >
              <Text style={styles.dcbUnsubConfirmText}>{t('iConfirm')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>

    {/* DCB Unsubscribe Loading Modal */}
    <Modal
      visible={isDcbUnsubscribing}
      transparent
      animationType="fade"
    >
      <View style={styles.dcbUnsubOverlay}>
        <View style={styles.dcbUnsubContent}>
          <ActivityIndicator size="large" color={primaryColor} />
          <Text style={[styles.dcbUnsubMessage, { marginTop: 16 }]}>{t('dcbUnsubscribeButton')}...</Text>
        </View>
      </View>
    </Modal>

    {/* DCB Unsubscribe Result Modal */}
    <Modal
      visible={dcbUnsubResult !== null}
      transparent
      animationType="fade"
      onRequestClose={() => setDcbUnsubResult(null)}
    >
      <View style={styles.dcbUnsubOverlay}>
        <View style={styles.dcbUnsubContent}>
          <Text style={styles.dcbUnsubTitle}>
            {dcbUnsubResult === 'success' ? t('dcbUnsubscribeConfirmTitle') : t('error')}
          </Text>
          <Text style={styles.dcbUnsubMessage}>
            {dcbUnsubResult === 'success' ? t('dcbUnsubscribeSuccess') : t('dcbUnsubscribeError')}
          </Text>
          <View style={styles.dcbUnsubButtons}>
            <TouchableOpacity
              style={[styles.dcbUnsubConfirmButton, { flex: 1, backgroundColor: primaryColor }]}
              onPress={() => setDcbUnsubResult(null)}
            >
              <Text style={styles.dcbUnsubConfirmText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>

    {/* Image Picker Modal */}
    <Modal
      visible={showImagePickerModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowImagePickerModal(false)}
    >
      <View style={styles.imagePickerOverlay}>
        <View style={styles.imagePickerContainer}>
          <View style={styles.imagePickerHeader}>
            <Text style={styles.imagePickerTitle}>{t('selectImageSource')}</Text>
            <TouchableOpacity onPress={() => setShowImagePickerModal(false)}>
              <X size={24} color="#ffffff" />
            </TouchableOpacity>
          </View>

          <View style={styles.imagePickerOptions}>
            {Platform.OS !== 'web' && (
              <TouchableOpacity
                style={styles.imagePickerOption}
                onPress={handleTakePhoto}
              >
                <Camera size={24} color={primaryColor} />
                <Text style={styles.imagePickerOptionText}>{t('takePhoto')}</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={styles.imagePickerOption}
              onPress={handlePickFromLibrary}
            >
              <ImageIcon size={24} color={primaryColor} />
              <Text style={styles.imagePickerOptionText}>{t('chooseFromLibrary')}</Text>
            </TouchableOpacity>

            {userProfile.profilePictureUrl && (
              <TouchableOpacity
                style={[styles.imagePickerOption, styles.removeOption]}
                onPress={() => {
                  setShowImagePickerModal(false);
                  handleRemoveProfilePicture();
                }}
              >
                <Trash2 size={24} color="#ff4444" />
                <Text style={[styles.imagePickerOptionText, styles.removeOptionText]}>
                  {t('removeProfilePicture')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>

    {/* Notification Settings Modal */}
    <Modal
      visible={showNotificationSettings}
      transparent
      animationType="slide"
      onRequestClose={() => setShowNotificationSettings(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.notificationModalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('notificationSettings')}</Text>
            <TouchableOpacity onPress={() => setShowNotificationSettings(false)}>
              <X size={24} color="#ffffff" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalContent}>
            {notificationPreferences && (
              <>
                {/* Guest user notice */}
                {!authState.user && (
                  <View style={[styles.guestNotice, { backgroundColor: `${primaryColor}1A`, borderColor: `${primaryColor}4D` }]}>
                    <Text style={[styles.guestNoticeText, { color: primaryColor }]}>
                      {t('signInToSavePreferences')}
                    </Text>
                  </View>
                )}

                {/* Push Notification Status */}
                <View style={styles.notificationSection}>
                  <View style={styles.notificationHeader}>
                    <Bell size={20} color={primaryColor} />
                    <Text style={styles.notificationSectionTitle}>{t('pushNotifications')}</Text>
                  </View>

                  {notificationPreferences.push_enabled ? (
                    <View style={styles.notificationStatusCard}>
                      <View style={styles.notificationStatusBadge}>
                        <Check size={16} color="#4CAF50" />
                      </View>
                      <View style={styles.notificationStatusContent}>
                        <Text style={styles.notificationStatusTitle}>{t('enabled')}</Text>
                        <Text style={styles.notificationStatusDescription}>
                          {t('pushNotificationsEnabledDescription')}
                        </Text>
                      </View>
                      <TouchableOpacity
                        style={[styles.settingsButton, { backgroundColor: `${primaryColor}33` }]}
                        onPress={handleOpenNotificationSettings}
                      >
                        <Settings size={20} color={primaryColor} />
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <View style={styles.notificationStatusCard}>
                      <View style={[styles.notificationStatusBadge, styles.disabledBadge]}>
                        <X size={16} color="#ff4444" />
                      </View>
                      <View style={styles.notificationStatusContent}>
                        <Text style={styles.notificationStatusTitle}>{t('disabled')}</Text>
                        <Text style={styles.notificationStatusDescription}>
                          {t('pushNotificationsDisabledDescription')}
                        </Text>
                      </View>
                      {notificationPreferences.can_request_permission && (
                        <TouchableOpacity
                          style={[styles.enableButton, { backgroundColor: primaryColor }]}
                          onPress={handleRequestPushPermission}
                          disabled={isLoadingPreferences}
                        >
                          <Text style={styles.enableButtonText}>
                            {isLoadingPreferences ? t('enabling') : t('enable')}
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                </View>
              </>
            )}
          </ScrollView>

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[styles.saveButton, { flex: 1, backgroundColor: primaryColor }]}
              onPress={() => setShowNotificationSettings(false)}
            >
              <Text style={styles.saveButtonText}>{t('done')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>

    {/* Local Storage Modal */}
    <Modal
      visible={showLocalStorageModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowLocalStorageModal(false)}
    >
      <View style={[styles.modalOverlay, isDesktopWeb && styles.modalOverlayDesktopCenter]}>
        <View style={[styles.localStorageModalContainer, isDesktopWeb && styles.localStorageModalContainerDesktop]}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('localStorage')}</Text>
            <TouchableOpacity onPress={() => setShowLocalStorageModal(false)}>
              <X size={24} color="#ffffff" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalContent}>
            {isLoadingCacheStats ? (
              <Text style={styles.loadingText}>{t('loading')}</Text>
            ) : cacheStats ? (
              <View style={styles.cacheStatsContainer}>
                <View style={styles.cacheStatRow}>
                  <ImageIcon size={16} color="#666" />
                  <Text style={styles.cacheStatLabel}>{t('cachedImages')}</Text>
                  <Text style={styles.cacheStatValue}>{cacheStats.totalImages}</Text>
                </View>

                <View style={styles.cacheStatRow}>
                  <Settings size={16} color="#666" />
                  <Text style={styles.cacheStatLabel}>{t('storageUsed')}</Text>
                  <Text style={styles.cacheStatValue}>{cacheStats.totalSizeMB} MB</Text>
                </View>

                {cacheStats.expiredCount > 0 && (
                  <View style={styles.cacheStatRow}>
                    <Text style={styles.cacheStatLabel}>{t('expiredImages')}</Text>
                    <Text style={[styles.cacheStatValue, styles.expiredCount]}>
                      {cacheStats.expiredCount}
                    </Text>
                  </View>
                )}

                <View style={styles.cacheButtonsContainer}>
                  <TouchableOpacity
                    style={[styles.cacheButton, styles.cacheButtonSecondary]}
                    onPress={handleClearExpiredCache}
                    disabled={isClearingCache}
                  >
                    <Text style={styles.cacheButtonTextSecondary}>
                      {isClearingCache ? t('clearing') : t('clearExpiredCache')}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.cacheButton, styles.cacheButtonDanger]}
                    onPress={handleClearCache}
                    disabled={isClearingCache}
                  >
                    <Trash2 size={16} color="#ffffff" />
                    <Text style={styles.cacheButtonText}>
                      {isClearingCache ? t('clearing') : t('clearAllCache')}
                    </Text>
                  </TouchableOpacity>
                </View>

                {cacheStats.oldestCacheDate && (
                  <Text style={styles.cacheHint}>
                    {t('cacheAutoClears30Days')}
                  </Text>
                )}
              </View>
            ) : null}
          </ScrollView>

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[styles.saveButton, { flex: 1, backgroundColor: primaryColor }]}
              onPress={() => setShowLocalStorageModal(false)}
            >
              <Text style={styles.saveButtonText}>{t('done')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>

    {/* Force Update Modal */}
    {updateCheckResult && (
      <ForceUpdateModal
        visible={showForceUpdateModal}
        storeUrl={updateCheckResult.storeUrl}
        currentVersion={updateCheckResult.currentVersion}
        minimumVersion={updateCheckResult.minimumVersion}
      />
    )}

    {/* Recommended Update Modal */}
    {updateCheckResult && updateCheckResult.recommendedVersion && (
      <RecommendedUpdateModal
        visible={showRecommendedUpdateModal}
        storeUrl={updateCheckResult.storeUrl}
        currentVersion={updateCheckResult.currentVersion}
        recommendedVersion={updateCheckResult.recommendedVersion}
        onDismiss={async () => {
          setShowRecommendedUpdateModal(false);
          if (updateCheckResult.recommendedVersion) {
            await appVersionService.dismissRecommendedUpdate(updateCheckResult.recommendedVersion);
          }
        }}
      />
    )}

    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  desktopContainer: {
    flex: 1,
    width: '100%',
  },
  nativeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xs,
  },
  nativeHeaderLogo: {
    width: 120,
    height: 32,
  },
  headerUserInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xs,
  },
  headerAvatarContainer: {
    position: 'relative',
  },
  headerAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#333',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  headerAvatarImage: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  headerCameraButton: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: radius.md,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: palette.neutral[700],
  },
  headerCameraButtonText: {
    color: palette.white,
    fontSize: 8,
  },
  headerAvatarLoadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 68, 68, 0.1)',
    marginHorizontal: spacing.lg,
    marginBottom: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: radius.md,
    gap: spacing.xs,
  },
  uploadErrorText: {
    color: '#ff4444',
    fontSize: typography.fontSize.sm,
    flex: 1,
  },
  uploadErrorDismissButton: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xxs,
    borderRadius: radius.sm,
    backgroundColor: 'rgba(255, 68, 68, 0.2)',
  },
  uploadErrorDismissText: {
    color: '#ff4444',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.weight.semibold,
  },
  headerUserName: {
    color: palette.white,
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.medium,
    maxWidth: 100,
  },
  headerEditButton: {
    padding: spacing.xxs,
  },
  userSection: {
    padding: spacing.lg,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  avatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#333',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  userDetails: {
    flex: 1,
  },
  userName: {
    color: palette.white,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.weight.semibold,
  },
  signInButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: palette.white,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: spacing.lg,
  },
  signInText: {
    color: palette.white,
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.medium,
  },
  profileLoginFormContainer: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: spacing.xl,
  },
  profileLoginFormContainerDesktop: {
    maxWidth: '50%',
    alignSelf: 'center',
    width: '100%',
  },
  dcbValidatingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    gap: spacing.md,
  },
  dcbValidatingText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.medium,
    color: '#AAAAAA',
    textAlign: 'center',
  },
  dcbErrorText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.medium,
    color: palette.semantic.error,
    textAlign: 'center',
    paddingHorizontal: spacing.xl,
  },
  gracePeriodBanner: {
    backgroundColor: 'rgba(255, 165, 0, 0.12)',
    borderRadius: radius.lg,
    padding: spacing.md,
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: 'rgba(255, 165, 0, 0.3)',
  },
  gracePeriodTitle: {
    color: '#FFA500',
    fontSize: 15,
    fontWeight: typography.weight.semibold,
    marginBottom: 6,
  },
  gracePeriodText: {
    color: '#ccc',
    fontSize: 13,
    lineHeight: 19,
  },
  dcbSubscriptionCard: {
    backgroundColor: 'rgba(168, 85, 247, 0.10)',
    borderRadius: radius.lg,
    padding: spacing.md,
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: 'rgba(168, 85, 247, 0.3)',
  },
  dcbSubscriptionTitle: {
    color: '#A855F7',
    fontSize: 15,
    fontWeight: typography.weight.semibold,
    marginBottom: 6,
  },
  dcbSubscriptionText: {
    color: '#ccc',
    fontSize: 13,
    lineHeight: 19,
  },
  notBilledDenyBanner: {
    backgroundColor: 'rgba(220, 38, 38, 0.12)',
    borderRadius: radius.lg,
    padding: spacing.md,
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: 'rgba(220, 38, 38, 0.3)',
  },
  notBilledDenyTitle: {
    color: '#EF4444',
    fontSize: 15,
    fontWeight: typography.weight.semibold,
    marginBottom: 6,
  },
  notBilledDenyText: {
    color: '#ccc',
    fontSize: 13,
    lineHeight: 19,
  },
  section: {
    paddingHorizontal: spacing.lg,
    marginBottom: 30,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    color: palette.white,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.weight.semibold,
  },
  menuSection: {
    paddingHorizontal: spacing.lg,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  dangerButton: {
    backgroundColor: palette.neutral[600],
    borderRadius: radius.lg,
    borderBottomWidth: 0,
    paddingHorizontal: spacing.md,
    marginTop: spacing.xs,
  },
  storageStatsContainer: {
    backgroundColor: palette.neutral[600],
    borderRadius: radius.lg,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  storageStatsTitle: {
    color: palette.white,
    fontSize: 15,
    fontWeight: typography.weight.semibold,
    marginBottom: spacing.sm,
    paddingBottom: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: '#444',
  },
  storageStatRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  storageStatLabel: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.medium,
    flex: 1,
  },
  storageStatValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
  },
  highlightValue: {
    color: '#4CAF50',
    fontSize: typography.fontSize.lg,
  },
  seriesListItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    backgroundColor: palette.neutral[700],
    borderRadius: 6,
    marginBottom: 6,
  },
  seriesIdText: {
    color: palette.white,
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.semibold,
    flex: 1,
  },
  seriesEpisodeCount: {
    color: '#4CAF50',
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.semibold,
    marginHorizontal: spacing.sm,
  },
  seriesAgeText: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.sm,
  },
  moreSeriesText: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
    paddingVertical: spacing.xs,
    fontStyle: 'italic',
  },
  episodeUrlItem: {
    backgroundColor: palette.neutral[700],
    borderRadius: 6,
    padding: spacing.sm,
    marginBottom: spacing.xs,
    borderLeftWidth: 3,
    borderLeftColor: '#4CAF50',
  },
  episodeIdText: {
    color: '#4CAF50',
    fontSize: 13,
    fontWeight: typography.weight.semibold,
    marginBottom: spacing.xxs,
  },
  episodeTitleText: {
    color: palette.white,
    fontSize: typography.fontSize.md,
    marginBottom: spacing.xs,
  },
  urlStatusRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: 6,
  },
  urlStatusLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.weight.semibold,
  },
  urlText: {
    color: palette.neutral[350],
    fontSize: 11,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    marginTop: 2,
  },
  menuText: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    flex: 1,
    marginLeft: spacing.md,
  },
  expandableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  expandableContent: {
    backgroundColor: palette.neutral[700],
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  subMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingLeft: 36,
    borderBottomWidth: 1,
    borderBottomColor: palette.neutral[600],
  },
  subMenuText: {
    color: '#ccc',
    fontSize: typography.fontSize.md,
    flex: 1,
    marginLeft: spacing.sm,
  },
  profileInfoContainer: {
    backgroundColor: palette.neutral[600],
    borderRadius: radius.lg,
    padding: spacing.md,
  },
  profileInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  profileInfoContent: {
    flex: 1,
    marginLeft: spacing.sm,
  },
  profileInfoLabel: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.sm,
    marginBottom: 2,
  },
  profileInfoValue: {
    color: palette.white,
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.medium,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'flex-end',
  },
  modalOverlayDesktopCenter: {
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: palette.neutral[600],
    borderTopLeftRadius: spacing.lg,
    borderTopRightRadius: spacing.lg,
    maxHeight: '90%',
    minHeight: '50%',
  },
  modalContainerDesktop: {
    maxWidth: '50%',
    alignSelf: 'center',
    width: '100%',
  },
  localStorageModalContainer: {
    backgroundColor: palette.neutral[600],
    borderTopLeftRadius: spacing.lg,
    borderTopRightRadius: spacing.lg,
    maxHeight: '60%',
    minHeight: '40%',
  },
  localStorageModalContainerDesktop: {
    maxWidth: '50%',
    alignSelf: 'center',
    width: '100%',
  },
  notificationModalContainer: {
    backgroundColor: palette.neutral[600],
    borderTopLeftRadius: spacing.lg,
    borderTopRightRadius: spacing.lg,
    maxHeight: '50%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  modalTitle: {
    color: palette.white,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.weight.semibold,
  },
  modalContent: {
    padding: spacing.lg,
  },
  modalContentContainer: {
    paddingBottom: 100,
  },
  inputContainer: {
    marginBottom: spacing.lg,
  },
  inputLabel: {
    color: palette.white,
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.medium,
    marginBottom: spacing.xs,
  },
  input: {
    backgroundColor: '#333',
    borderRadius: radius.md,
    padding: spacing.sm,
    color: palette.white,
    fontSize: typography.fontSize.base,
    borderWidth: 1,
    borderColor: '#444',
  },
  inputHint: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xxs,
  },
  modalActions: {
    flexDirection: 'row',
    padding: spacing.lg,
    gap: spacing.sm,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: palette.neutral[450],
    borderRadius: radius.md,
    padding: spacing.sm,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.medium,
  },
  saveButton: {
    flex: 1,
    borderRadius: radius.md,
    padding: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  saveButtonText: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  dateText: {
    color: palette.white,
    fontSize: typography.fontSize.base,
  },
  placeholderText: {
    color: palette.neutral[450],
  },
  datePickerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  datePickerModal: {
    backgroundColor: palette.neutral[600],
    borderRadius: radius.lg,
    margin: spacing.lg,
    overflow: 'hidden',
  },
  datePickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  datePickerTitle: {
    color: palette.white,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.weight.semibold,
  },
  datePickerButton: {
    paddingHorizontal: 15,
    paddingVertical: spacing.xs,
  },
  datePickerButtonText: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.base,
  },
  datePickerOkButton: {
    fontWeight: typography.weight.semibold,
  },
  datePicker: {
    backgroundColor: palette.neutral[600],
    color: palette.white,
  },
  biometricContainer: {
    backgroundColor: palette.neutral[600],
    borderRadius: radius.lg,
    padding: spacing.md,
  },
  biometricItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  biometricIcon: {
    width: 40,
    height: 40,
    borderRadius: radius.full,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  biometricContent: {
    flex: 1,
  },
  biometricTitle: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
    marginBottom: spacing.xxs,
  },
  biometricDescription: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.md,
  },
  biometricToggle: {
    width: 50,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#444',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  biometricToggleActive: {
  },
  biometricToggleThumb: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: palette.white,
    alignSelf: 'flex-start',
  },
  biometricToggleThumbActive: {
    alignSelf: 'flex-end',
  },
  deleteModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  deleteModalContainer: {
    backgroundColor: palette.neutral[600],
    borderRadius: radius.xl,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    overflow: 'hidden',
  },
  deleteModalHeader: {
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  deleteModalTitle: {
    color: palette.white,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.weight.semibold,
    textAlign: 'center',
  },
  deleteModalContent: {
    padding: spacing.lg,
    maxHeight: 400,
  },
  deleteModalWarning: {
    color: '#ccc',
    fontSize: typography.fontSize.md,
    lineHeight: 22,
  },
  deleteModalActions: {
    flexDirection: 'row',
    padding: spacing.lg,
    gap: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: '#333',
  },
  deleteCancelButton: {
    flex: 1,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: palette.neutral[450],
    borderRadius: radius.md,
    padding: 14,
    alignItems: 'center',
  },
  deleteCancelButtonText: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.medium,
  },
  deleteConfirmButton: {
    flex: 1,
    borderRadius: radius.md,
    padding: 14,
    alignItems: 'center',
  },
  deleteConfirmButtonText: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
  },
  versionContainer: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xl,
    alignItems: 'center',
  },
  versionText: {
    color: palette.neutral[450],
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
  },
  editPseudoButton: {
    padding: spacing.xs,
    marginLeft: spacing.xs,
  },
  errorText: {
    color: '#ff4444',
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xxs,
  },
  avatarContainer: {
    position: 'relative',
    marginRight: spacing.md,
  },
  avatarImage: {
    width: 60,
    height: 60,
    borderRadius: 30,
  },
  cameraButton: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: palette.neutral[700],
  },
  cameraButtonText: {
    color: palette.white,
    fontSize: typography.fontSize.sm,
  },
  imagePickerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'flex-end',
  },
  imagePickerContainer: {
    backgroundColor: palette.neutral[600],
    borderTopLeftRadius: spacing.lg,
    borderTopRightRadius: spacing.lg,
    paddingBottom: spacing.lg,
  },
  imagePickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  imagePickerTitle: {
    color: palette.white,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.weight.semibold,
  },
  imagePickerOptions: {
    padding: spacing.lg,
  },
  imagePickerOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: '#333',
    borderRadius: radius.lg,
    marginBottom: spacing.sm,
  },
  imagePickerOptionText: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    marginLeft: spacing.md,
    fontWeight: typography.weight.medium,
  },
  removeOption: {
    backgroundColor: 'rgba(255, 68, 68, 0.1)',
  },
  removeOptionText: {
    color: '#ff4444',
  },
  guestNotice: {
    borderRadius: radius.lg,
    padding: spacing.md,
    marginBottom: spacing.xl,
    borderWidth: 1,
  },
  guestNoticeText: {
    fontSize: typography.fontSize.md,
    lineHeight: 20,
    textAlign: 'center',
  },
  notificationSection: {
    marginBottom: spacing.xl,
  },
  notificationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  notificationSectionTitle: {
    color: palette.white,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.weight.semibold,
    marginLeft: spacing.xs,
  },
  notificationSectionDescription: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.md,
    marginBottom: spacing.md,
    lineHeight: 20,
  },
  notificationStatusCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#333',
    borderRadius: radius.lg,
    padding: spacing.md,
    marginTop: spacing.xs,
  },
  notificationStatusBadge: {
    width: 32,
    height: 32,
    borderRadius: radius.xl,
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  disabledBadge: {
    backgroundColor: 'rgba(255, 68, 68, 0.2)',
  },
  notificationStatusContent: {
    flex: 1,
  },
  notificationStatusTitle: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
    marginBottom: spacing.xxs,
  },
  notificationStatusDescription: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.md,
    lineHeight: 18,
  },
  settingsButton: {
    width: 40,
    height: 40,
    borderRadius: radius.full,
    justifyContent: 'center',
    alignItems: 'center',
  },
  enableButton: {
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  enableButtonText: {
    color: palette.white,
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.semibold,
  },
  notificationPreferences: {
    backgroundColor: '#333',
    borderRadius: radius.lg,
    overflow: 'hidden',
  },
  preferenceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: '#444',
  },
  preferenceContent: {
    flex: 1,
    marginRight: spacing.sm,
  },
  preferenceTitle: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
    marginBottom: spacing.xxs,
  },
  preferenceDescription: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.md,
    lineHeight: 18,
  },
  preferenceToggle: {
    width: 50,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#444',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  preferenceToggleActive: {
  },
  preferenceToggleThumb: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: palette.white,
    alignSelf: 'flex-start',
  },
  preferenceToggleThumbActive: {
    alignSelf: 'flex-end',
  },
  cacheStatsContainer: {
    backgroundColor: '#333',
    borderRadius: radius.lg,
    padding: spacing.md,
  },
  debugSection: {
    marginBottom: spacing.lg,
    paddingBottom: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: '#444',
  },
  debugSectionTitle: {
    color: '#FF6B6B',
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
    marginBottom: spacing.sm,
  },
  debugText: {
    color: '#ccc',
    fontSize: typography.fontSize.md,
    lineHeight: 20,
    marginBottom: spacing.xs,
  },
  cacheStatRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
    gap: spacing.xs,
  },
  cacheStatLabel: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.md,
    flex: 1,
  },
  cacheStatValue: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
  },
  expiredCount: {
    color: '#ff9800',
  },
  cacheButtonsContainer: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  cacheButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.sm,
    borderRadius: radius.md,
    gap: spacing.xs,
  },
  cacheButtonSecondary: {
    backgroundColor: '#444',
  },
  cacheButtonDanger: {
    backgroundColor: '#ff4444',
  },
  cacheButtonText: {
    color: palette.white,
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.semibold,
  },
  cacheButtonTextSecondary: {
    color: palette.white,
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.semibold,
  },
  cacheHint: {
    color: palette.neutral[450],
    fontSize: typography.fontSize.sm,
    marginTop: spacing.sm,
    textAlign: 'center',
  },
  loadingText: {
    color: palette.neutral[350],
    fontSize: typography.fontSize.md,
    textAlign: 'center',
    padding: spacing.md,
  },
  debugTrackingToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#2a3a2a',
    borderRadius: radius.lg,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: '#28a745',
  },
  debugTrackingToggleDisabled: {
    backgroundColor: '#3a2a2a',
    borderColor: '#dc3545',
  },
  debugTrackingToggleContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  debugTrackingToggleText: {
    flex: 1,
  },
  debugTrackingToggleTitle: {
    color: palette.white,
    fontSize: 15,
    fontWeight: typography.weight.semibold,
    marginBottom: 2,
  },
  debugTrackingToggleStatus: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.weight.medium,
  },
  debugTrackingStatusOn: {
    color: '#28a745',
  },
  debugTrackingStatusOff: {
    color: '#dc3545',
  },
  debugTrackingToggleIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  indicatorOn: {
    backgroundColor: '#28a745',
  },
  indicatorOff: {
    backgroundColor: '#dc3545',
  },
  dcbUnsubOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  dcbUnsubContent: {
    backgroundColor: palette.neutral[600],
    borderRadius: radius.xl,
    padding: spacing.xl,
    width: '100%',
    maxWidth: 400,
  },
  dcbUnsubTitle: {
    color: palette.white,
    fontSize: typography.fontSize.xl,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  dcbUnsubMessage: {
    color: '#ccc',
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  dcbUnsubButtons: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  dcbUnsubCancelButton: {
    flex: 1,
    backgroundColor: '#444',
    paddingVertical: 14,
    borderRadius: spacing.sm - 2,
    alignItems: 'center',
  },
  dcbUnsubCancelText: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
  },
  dcbUnsubConfirmButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: spacing.sm - 2,
    alignItems: 'center',
  },
  dcbUnsubConfirmText: {
    color: palette.white,
    fontSize: typography.fontSize.base,
    fontWeight: '700',
  },
  paymentBanner: {
    width: '100%',
    backgroundColor: palette.neutral[600],
    borderRadius: radius.lg,
    padding: spacing.lg,
    alignItems: 'center',
    borderWidth: 1,
    marginBottom: spacing.md,
  },
  paymentBannerSuccess: {
    width: '100%',
    borderRadius: radius.lg,
    padding: spacing.lg,
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  paymentBannerTitle: {
    color: palette.white,
    fontSize: typography.fontSize.lg,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 6,
  },
  paymentBannerMessage: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: typography.fontSize.md,
    textAlign: 'center',
    marginBottom: spacing.md,
    lineHeight: 20,
  },
  paymentBannerButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.sm - 2,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  paymentBannerButtonText: {
    color: palette.white,
    fontSize: 15,
    fontWeight: typography.weight.semibold,
  },
});

export default function ProfileScreen() {
  return (
    <>
      <SEOHead
        title="Mon profil - Hekaya | Compte et parametres"
        description="Gerez votre compte Hekaya : abonnement, preferences de langue, historique de visionnage et parametres de notification pour votre experience streaming."
        path="/profile"
      />
      <ProfileScreenMobile />
    </>
  );
}